// Moves.jsx - Enhanced version with nested variations support
import React, { useEffect, useContext } from 'react';
import useGameLogic from './UseGameState';
import { useGameState } from './GameStateContext';
import { Chess } from 'chess.js';
import constants from '../../util/constants';
import clsx from 'clsx';
import { MobileContext } from '../../context/MobileContext';
import { 
  getNodeAtPathInfinite,
  getPathDepth,
  parseVariationPath,
  getParentPath,
  isInVariation as isInVariationPath,
  getMoveSequenceToPath
} from '../../util/VariationPathUtils';
import { getStoredFENFromMoves } from './UseGameState';
import { isProfane, cleanProfanity } from '../../util/filterProfanity';

const CHESSBOARD = constants.CHESSBOARD;

// Helper function to get responsive styles based on mobile context
const getMobileStyles = (isMobile) => ({
  moveButton: {
    fontSize: isMobile ? '1rem' : '0.95rem',    // Slightly larger for touch
    minWidth: isMobile ? 44 : 36,               // 44px minimum touch target
    padding: isMobile ? '6px 10px' : '4px 8px', // More padding for mobile
  },
  moveNumber: {
    fontSize: isMobile ? '1rem' : '0.9rem',     // Slightly larger for readability  
    minWidth: isMobile ? 32 : 24,               // Better visibility on mobile
  },
  smallIcon: {
    fontSize: isMobile ? '1rem' : '0.9rem',     // Larger icons for mobile
  }
});

/**
 * Puzzle-specific moves component that only shows moves up to the current index
 * @param {Object} props - Component props
 * @param {boolean} props.isPuzzle - Whether this is a puzzle mode
 * @param {number} props.maxMoveIndex - Maximum move index to show (current position)
 * @param {Array} props.revealedMoves - Array of move indices that have been revealed
 * @returns {JSX.Element} - Puzzle moves component
 */
const PuzzleMoves = ({ maxMoveIndex = -1, revealedMoves = [], moves, currentMove, goToMove, initialFEN }) => {
  const isMobile = useContext(MobileContext);
  const mobileStyles = getMobileStyles(isMobile);
  
  if (!moves || moves.length === 0) return <div></div>;

  // Filter moves to only show revealed ones
  const visibleMoves = moves.filter((move, index) => {
    // Show moves that are in the revealedMoves array or up to maxMoveIndex
    return revealedMoves.includes(index) || index <= maxMoveIndex;
  });

  // If no moves are visible yet, show a placeholder
  if (visibleMoves.length === 0) {
    return (
      <div style={{ 
        padding: 12,
        background: '#252525',
        borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        border: '2px solid #3066be'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          padding: '20px',
          color: '#888',
          fontStyle: 'italic'
        }}>
          Make your first move to begin...
        </div>
      </div>
    );
  }

  /**
   * Helper to determine if a move ID matches the current move
   * @param {string} id - Move ID to check
   * @returns {boolean} - True if current
   */
  const isCurrent = (id) => {
    // Direct exact match - this is the primary case
    if (id === currentMove) return true;
    
    // Don't prevent clicks on different moves - only prevent clicking the exact same move
    return false;
  };

  /**
   * Determines if a move has variations
   * @param {Object} move - Move object
   * @returns {boolean} - True if has variations
   */
  const hasVariations = (move) => {
    return move?.variations && move.variations.length > 0;
  };

  /**
   * Helper to determine the starting color at a particular node in the move tree
   * Uses chess.js to calculate the actual starting color based on FEN
   * @param {string} path - The path to the position
   * @returns {string} - 'w' for white to move, 'b' for black to move
   */
  const getStartColor = (path) => {
    if (!path || path === '-1') {
      // For the initial position, check if we have a custom FEN
      if (initialFEN) {
        try {
          const chess = new Chess(initialFEN);
          return chess.turn();
        } catch (e) {
          console.error('Invalid initial FEN:', initialFEN);
          return 'w';
        }
      }
      return 'w'; // Default starting position is white to move
    }
    
    // Initialize a new chess instance with initial FEN if provided
    const chess = initialFEN ? new Chess(initialFEN) : new Chess();
    
    // Calculate the position up to the parent move
    const pathParts = path.split('|');
    const mainLine = parseInt(pathParts[0], 10);
    
    // Apply main line moves up to the parent move
    for (let i = 0; i <= mainLine && i < visibleMoves.length; i++) {
      if (visibleMoves[i]?.notation) {
        try {
          chess.move(visibleMoves[i].notation);
        } catch (e) {
          console.error(`Invalid move ${visibleMoves[i].notation} at index ${i}`, e);
        }
      }
    }
    
    return chess.turn();
  };

  /**
   * Calculate move number for display
   * @param {string} path - The path to the position
   * @param {number} index - Move index
   * @returns {number} - Move number for display
   */
  const calculateMoveNumber = (path, index) => {
    const startColor = getStartColor(path);
    
    if (startColor === 'b') {
      // Black starts - adjust numbering
      return Math.floor((index + 1) / 2) + 1;
    } else {
      // White starts - normal numbering
      return Math.floor(index / 2) + 1;
    }
  };

  /**
   * Get trios for the visible moves
   * @param {Array} moves - Array of moves
   * @param {string} startWith - Starting color ('w' or 'b')
   * @returns {Array} - Array of trio objects
   */
  const getTrios = (moves, startWith = 'w') => {
    const trios = [];
    let currentTrio = null;
    let moveNumber = 1;

    if (startWith === 'b') {
      // Black starts - first trio has only black move
      for (let i = 0; i < moves.length; i++) {
        const isWhite = i % 2 === 1; // Flipped for black start
        
        if (isWhite) {
          currentTrio = {
            moveNumber,
            whiteMove: moves[i],
            whiteIndex: i,
            blackMove: null,
            blackIndex: null
          };
          trios.push(currentTrio);
        } else {
          if (i === 0) {
            // First move is black
            currentTrio = {
              moveNumber,
              whiteMove: null,
              whiteIndex: null,
              blackMove: moves[i],
              blackIndex: i
            };
            trios.push(currentTrio);
            moveNumber++;
          } else if (currentTrio) {
            currentTrio.blackMove = moves[i];
            currentTrio.blackIndex = i;
            moveNumber++;
          }
        }
      }
    } else {
      // White starts - normal trio processing
      for (let i = 0; i < moves.length; i++) {
        const isWhite = i % 2 === 0;
        
        if (isWhite) {
          currentTrio = {
            moveNumber,
            whiteMove: moves[i],
            whiteIndex: i,
            blackMove: null,
            blackIndex: null
          };
          trios.push(currentTrio);
        } else {
          if (currentTrio) {
            currentTrio.blackMove = moves[i];
            currentTrio.blackIndex = i;
            moveNumber++;
          }
        }
      }
    }

    return trios;
  };

  /**
   * Focus on the trio containing the current move
   * @param {Array} moves - Array of moves
   * @param {number} focusIndex - Index to focus on
   * @param {string} startColor - Starting color
   * @returns {Object} - Object with prev, current, next trios
   */
  const focusTrio = (moves, focusIndex, startColor) => {
    const trios = getTrios(moves, startColor);
    
    if (trios.length === 0) {
      return { prev: null, current: null, next: null };
    }
    
    // Find the trio containing the focus index
    let currentTrioIndex = -1;
    for (let i = 0; i < trios.length; i++) {
      const trio = trios[i];
      if ((trio.whiteIndex !== null && trio.whiteIndex === focusIndex) ||
          (trio.blackIndex !== null && trio.blackIndex === focusIndex)) {
        currentTrioIndex = i;
        break;
      }
    }
    
    // If no trio found, default to the last trio
    if (currentTrioIndex === -1) {
      currentTrioIndex = trios.length - 1;
    }
    
    return {
      prev: currentTrioIndex > 0 ? trios[currentTrioIndex - 1] : null,
      current: trios[currentTrioIndex] || null,
      next: currentTrioIndex < trios.length - 1 ? trios[currentTrioIndex + 1] : null
    };
  };

  /**
   * Render a move button
   * @param {Object} move - Move object
   * @param {string} id - Move ID
   * @param {string} highlightColor - Highlight color
   * @returns {JSX.Element} - Move button element
   */
  const renderMoveButton = (move, id, highlightColor = '#444') => {
    const isCurrentMove = isCurrent(id);
    
    return (
      <button
        key={id}
        onClick={() => goToMove(id)}
        style={{
          background: isCurrentMove ? '#ff6600' : highlightColor,
          color: isCurrentMove ? 'white' : '#ccc',
          padding: mobileStyles.moveButton.padding,
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer',
          fontSize: mobileStyles.moveButton.fontSize,
          minWidth: mobileStyles.moveButton.minWidth,
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (!isCurrentMove) {
            e.target.style.background = '#666';
          }
        }}
        onMouseLeave={(e) => {
          if (!isCurrentMove) {
            e.target.style.background = highlightColor;
          }
        }}
      >
        {move.notation}
      </button>
    );
  };

  /**
   * Render a trio of moves
   * @param {Object} trio - Trio object
   * @param {string} baseId - Base ID for move paths
   * @param {string} prefixColor - Color for the trio
   * @returns {JSX.Element} - Trio element
   */
  const renderTrio = (trio, baseId, prefixColor = '#ccc') => {
    if (!trio) return null;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ 
          color: prefixColor, 
          fontSize: mobileStyles.moveNumber.fontSize, 
          minWidth: mobileStyles.moveNumber.minWidth,
          textAlign: 'right'
        }}>
          {trio.moveNumber}.
        </span>
        
        {trio.whiteMove ? (
          renderMoveButton(trio.whiteMove, `${baseId}${trio.whiteIndex}`, '#666') // Gray for white moves
        ) : (
          <div style={{ minWidth: mobileStyles.moveButton.minWidth }}></div>
        )}
        
        {trio.blackMove ? (
          renderMoveButton(trio.blackMove, `${baseId}${trio.blackIndex}`, '#666') // Green for black moves
        ) : (
          <div style={{ minWidth: mobileStyles.moveButton.minWidth }}></div>
        )}
      </div>
    );
  };


  
  // Check if the starting position has black to move
  const rootStartColor = initialFEN ? (() => {
    try {
      const chess = new Chess(initialFEN);
      return chess.turn();
    } catch (e) {
      return 'w';
    }
  })() : 'w';

  // Split the current move ID into path parts
  const pathParts = currentMove.split('|');
  
  // For puzzle mode, we work with visible moves only
  const currentIndex = parseInt(currentMove);
  const currentFocus = focusTrio(visibleMoves, currentIndex, rootStartColor);

  return (
    <div style={{ 
      padding: 12,
      background: '#252525',
      borderRadius: 4,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      border: '2px solid #3066be'
    }}>
     
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* The Classic Trio Display (Previous, Current, Next) */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          gap: 24, 
          justifyContent: 'space-around',
          padding: '8px 12px',
          borderRadius: 4,
         
        }}>
          {/* START button at the beginning */}
          {!currentFocus.prev && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button 
                onClick={() => {
                  
                  goToMove('-1');
                }}
                style={{
                  background: currentMove === '-1' ? '#58b047' : '#666',
                  color: 'white',
                  padding: mobileStyles.moveButton.padding,
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: mobileStyles.moveButton.fontSize,
                  minWidth: mobileStyles.moveButton.minWidth
                }}
              >
                START
              </button>
            </div>
          )}
          
          {currentFocus.prev && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {renderTrio(currentFocus.prev, '', '#999')}
            </div>
          )}
          
          {currentFocus.current && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {renderTrio(currentFocus.current, '', '#ccc')}
            </div>
          )}
          
          {currentFocus.next && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {renderTrio(currentFocus.next, '', '#888')}
            </div>
          )}

          {/* END marker at the end */}
          {!currentFocus.next && visibleMoves.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div 
                style={{
                  background: visibleMoves.length === parseInt(currentMove) + 1 ? '#ff0050' : '#666',
                  color: 'white',
                  padding: mobileStyles.moveButton.padding,
                  borderRadius: 4,
                  fontSize: mobileStyles.moveButton.fontSize,
                  minWidth: mobileStyles.moveButton.minWidth,
                  textAlign: 'center'
                }}
              >
                END
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Moves = ({ 
  undo = false, 
  isPuzzle = false, 
  maxMoveIndex = -1, 
  revealedMoves = [],
  // NEW COMMENT-RELATED PROPS
  type = 'DEFAULT',           // 'DEFAULT', 'COMMENTS', 'COMMENT_PREVIEW', 'COMMENT_FULL'
  userId = null,              // Comment ID to filter by
  commentPreviewMoves = null, // For preview functionality
  setCommentPreviewMoves = null,
  isAnnotationPreview = false,
  handleVariationNavigation = null,
  starredMoves = [],
  setCurrentMove = null,
  currentMoveIdx = null,      // Current move index for starring and navigation
  variationToComment = null,
  height = 480,               // Dynamic height prop with default
  isViewGame = false,         // NEW: Only show variation captions and move content in ViewGame
  // NEW CUSTOMIZATION PROPS FOR 2ADDGAME
  moveContent = '',           // Current move content for editing
  setMoveContent = null,      // Function to update move content
  variationContent = '',      // Current variation content for editing
  setVariationContent = null, // Function to update variation content
  updateContentForMove = null, // Function to save move content
  updateVariationContent = null, // Function to save variation content
  isAnnotationFocused = false, // Whether annotation is currently focused
  setIsAnnotationFocused = null, // Function to set annotation focus
  setSkipMoveIndexUpdate = null, // Function to skip move index updates
  isMoveInVariation = null,   // Function to check if current move is in variation
  // NEW STAR FUNCTIONALITY PROPS
  dispatch = null,            // Dispatch function for starring moves
  // NEW COMMENTS ARRAY PROP
  comments = [],              // Array of comment objects with id and content
  // NEW TEMPORARY CONTROLS PROP
  temporaryControls = null,    // Object with onDelete and onUndo functions
  // STARTING TURN PROP
  startingTurn = null,        // Starting turn override ('w' or 'b')
  // CUSTOM ANNOTATION STYLING PROP
  annotationStyleOverride = null  // Custom style object for annotation textarea
}) => {
  // Always call hooks at the top level
  const { moves, currentMove, goToMove, initialFEN } = useGameLogic();
  
  // Get mobile context for responsive styling
  const isMobile = useContext(MobileContext);
  const { state } = useGameState(); // Access game state for starting position variations
  
  // Get responsive styles
  const mobileStyles = getMobileStyles(isMobile);

 

  // Add comprehensive data logging function
  const logComprehensiveGameData = () => {
    
    
    
    // Basic game state
    
    
    // Main line moves analysis
    
    moves.forEach((move, index) => {
      if (move) {
        
        
        // Log variations if they exist
        if (move.variations && move.variations.length > 0) {
          
          move.variations.forEach((variation, varIdx) => {
            
          });
        }
      }
    });
    
    // Comment moves analysis
    
    const commentMoves = findCommentMovesInVariations(moves);
    
    commentMoves.forEach((commentMove, index) => {
      
    });
    
    // Current position analysis
    
    try {
      const currentMoveData = moves[parseInt(currentMove)] || null;
     
      
      // If it's a variation move, analyze the variation path
      if (currentMove.includes('|')) {
        
        const parts = currentMove.split('|');
        const mainIdx = parseInt(parts[0]);
        const [varIdx, moveIdx] = parts[1].split(':').map(Number);
        
        const parentMove = moves[mainIdx];
        const variation = parentMove?.variations?.[varIdx];
        const variationMove = variation?.[moveIdx];
        
        
      }
    } catch (error) {
      console.error('Error analyzing current position:', error);
    }
    
    
  };

  // Add useEffect to log data when moves change
  useEffect(() => {
    if (moves && moves.length > 0) {
      logComprehensiveGameData();
    }
  }, [moves, currentMove]);

  // NEW: Add useEffect to debug comments prop changes
  useEffect(() => {
    
  }, [comments]);

  // Add manual trigger function to window for debugging
  useEffect(() => {
    window.logGameData = logComprehensiveGameData;
    return () => {
      delete window.logGameData;
    };
  }, [moves, currentMove]);

  // COMMENT DETECTION FUNCTIONS
  /**
   * Check if a move is a comment move
   * @param {Object} move - The move object to check
   * @returns {boolean} - True if the move is a comment move
   */
  const isCommentMove = (move) => {
    if (!move || !move.creator) return false;
    const isComment = move.creator.type === CHESSBOARD.MOVE_TYPES.COMMENT;
    
    // Only log once per unique move to avoid spam
    if (isComment && !move._loggedAsComment) {
      
      move._loggedAsComment = true; // Mark as logged to prevent spam
    }
    
    return isComment;
  };

  /**
   * Deep scan function to find comment moves in nested variations
   * @param {Array} movesArray - Array of moves to scan
   * @returns {Array} - Array of found comment moves with their paths
   */
  const findCommentMovesInVariations = (movesArray) => {
    const result = [];

    if (!movesArray || !movesArray.length) return result;

    // Helper function to recursively scan through variations
    const scanForCommentMoves = (moves, path = "") => {
      if (!moves || !moves.length) return;

      moves.forEach((move, moveIndex) => {
        const currentPath = path ? `${path}${moveIndex}` : `${moveIndex}`;

        // Check if this move is a comment move
        if (isCommentMove(move)) {
          result.push({
            path: currentPath,
            commentId: move.creator.id,
            notation: move.notation,
            move: move
          });
          
        }

        // Check variations recursively
        if (move.variations && move.variations.length) {
          move.variations.forEach((variation, varIndex) => {
            scanForCommentMoves(variation, `${currentPath}|${varIndex}:`);
          });
        }
      });
    };

    scanForCommentMoves(movesArray);
    
    if (result.length > 0) {
      
    }
    
    return result;
  };

  /**
   * Filter moves to show only comment moves for a specific user
   * @param {Array} moves - Array of moves to filter
   * @param {number} commentId - Comment ID to filter by
   * @returns {Array} - Filtered moves
   */
  const filterMovesForComment = (moves, commentId) => {
    if (!commentId && commentId !== -1) return moves;
    
    // We need to search through variations since comment moves are stored there
    const commentMoves = findCommentMovesInVariations(moves);
    
    // Filter by the specific comment ID
    const filtered = commentMoves.filter(commentMove => 
      commentMove.commentId === commentId
    );
    
    if (filtered.length > 0) {
      
    } else {
      
    }
    
    return filtered;
  };

  /**
   * Check if the current position has any comments (linked comments or comment variations)
   * @param {string} moveIndex - Current move index
   * @param {Array} comments - Array of comment objects
   * @param {Array} moves - Array of moves (optional, for checking comment variations)
   * @returns {boolean} - True if position has any comments
   */
  const hasLinkedComments = (moveIndex, comments, moves = null) => {
    if (!moveIndex) {
      return false;
    }
    
    // Check for move-linked comments
    const hasLinked = comments && Array.isArray(comments) && comments.some(comment => 
      comment.moveIndex === moveIndex && 
      comment.moveIndex !== undefined && 
      comment.moveIndex !== null
    );
    
    // Check for comment variations (if moves array is provided)
    const hasVariations = moves ? getCommentVariationsCount(moveIndex, moves) > 0 : false;
    
    return hasLinked || hasVariations;
  };

  /**
   * Get the count of linked comments at current position
   * @param {string} moveIndex - Current move index  
   * @param {Array} comments - Array of comment objects
   * @returns {number} - Count of linked comments
   */
  const getLinkedCommentsCount = (moveIndex, comments) => {
    if (!comments || !Array.isArray(comments) || !moveIndex) {
      return 0;
    }
    
    return comments.filter(comment => 
      comment.moveIndex === moveIndex && 
      comment.moveIndex !== undefined && 
      comment.moveIndex !== null
    ).length;
  };

  /**
   * Get the count of comment variations at current position
   * @param {string} moveIndex - Current move index
   * @param {Array} moves - Array of moves
   * @returns {number} - Count of comment variations
   */
  const getCommentVariationsCount = (moveIndex, moves) => {
    if (!moves || !Array.isArray(moves) || !moveIndex) {
      return 0;
    }
    
    // For start position, there are no comment variations
    if (moveIndex === '-1') {
      return 0;
    }
    
    // If we're in a variation, we need to get the actual move at this variation position
    if (moveIndex.includes('|')) {
      try {
        const nodeResult = getNodeAtPathInfinite(moves, moveIndex);
        if (!nodeResult.success || !nodeResult.node?.variations) {
          return 0;
        }
        
        // Count variations that contain comment moves from THIS specific variation move
        return nodeResult.node.variations.filter(variation => 
          variation.some(varMove => 
            varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
          )
        ).length;
      } catch (error) {
        return 0;
      }
    } else {
      // For main line moves, use the original logic
      const mainLineIndex = parseInt(moveIndex, 10);
      
      if (isNaN(mainLineIndex) || mainLineIndex < 0 || mainLineIndex >= moves.length) {
        return 0;
      }
      
      const move = moves[mainLineIndex];
      if (!move?.variations) {
        return 0;
      }
      
      // Count variations that contain comment moves
      return move.variations.filter(variation => 
        variation.some(varMove => 
          varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
        )
      ).length;
    }
  };

  /**
   * Get the total count of all comments (linked + variations) at current position
   * @param {string} moveIndex - Current move index
   * @param {Array} comments - Array of comment objects
   * @param {Array} moves - Array of moves
   * @returns {number} - Total count of comments
   */
  const getTotalCommentsCount = (moveIndex, comments, moves) => {
    const linkedCount = getLinkedCommentsCount(moveIndex, comments);
    const variationsCount = getCommentVariationsCount(moveIndex, moves);
    return linkedCount + variationsCount;
  };

  // // CONSOLE LOGGING FOR MOVES ARRAY ANALYSIS
  // React.useEffect(() => {
  //   if (moves && moves.length > 0) {
      

  //     // Check each move for comment type
  //     moves.forEach((move, index) => {
  //       if (isCommentMove(move)) {
          
  //       }
  //     });

  //     // Scan for comment moves in variations
  //     const commentMoves = findCommentMovesInVariations(moves);
  //     if (commentMoves.length > 0) {
        
  //     }
  //   }
  // }, [moves, type, userId]);

  
 /**
   * Helper to determine if a move ID matches the current move for styling purposes
   * @param {string} id - Move ID to check
   * @returns {boolean} - True if should be styled as current
   */
  const isCurrentForStyling = (id) => {
    // Only do exact matches - this fixes the variation highlighting bug
    // where the first move in a variation would stay highlighted
    return id === currentMove;
  };

  /**
   * Helper to determine if a move ID is exactly the current move (for click prevention)
   * @param {string} id - Move ID to check
   * @returns {boolean} - True if exactly current
   */
  const isExactCurrentMove = (id) => {
    return id === currentMove;
  };


  /**
   * Determines the appropriate background color for a move button
   * @param {Object} move - Move object
   * @param {string} id - Move ID
   * @param {string} defaultColor - Default color for non-current moves
   * @returns {string} - Background color
   */
  const getMoveBackgroundColor = (move, id, defaultColor) => {
    const isCurrentMove = isCurrentForStyling(id);
    
    if (!isCurrentMove) {
      // Non-current moves: use default color (gray for comment moves, passed defaultColor for others)
      if (isCommentMove(move)) {
        return '#666'; // Gray for non-current comment moves
      }
      return defaultColor;
    }
    
    // Current moves: determine color based on move type and location
    if (isCommentMove(move)) {
      // Current comment moves: orange
      return '#ff6600';
    }
    
    if (id.includes('|')) {
      // Current variation moves (non-comment): gold
      return '#ffd700';
    }
    
    // Current main line moves: green
    return '#58b047';
  };

  // CONDITIONAL RENDERING BASED ON TYPE
  if (type === 'COMMENT_PREVIEW') {
    
    
    // Find variations containing comment moves for the specified userId (supports nested)
    let variationsWithCommentId = [];
    
    if (moves && moves.length > 0) {
      // Use the existing recursive function to find all comment moves
      const allCommentMoves = findCommentMovesInVariations(moves);
      const relevantCommentMoves = allCommentMoves.filter(commentMove => commentMove.commentId === userId);
      
      
      
      // console.log('[3Moves COMMENT_PREVIEW] Raw comment move paths for userId', userId, ':', 
      //   relevantCommentMoves.map(cm => cm.path));
      
      // Group comment moves by their variation path to avoid duplicate variation previews
      const variationGroups = new Map();
      
      relevantCommentMoves.forEach(commentMove => {
        const pathParts = commentMove.path.split('|');
        
        if (pathParts.length >= 2) {
          // This is in a variation - get the parent and variation info
          const mainMoveIdx = parseInt(pathParts[0], 10);
          const variationPath = pathParts.slice(0, -1).join('|'); // Remove the last segment
          
          // For first-level variations
          if (pathParts.length === 2) {
            const [varIdx] = pathParts[1].split(':').map(Number);
            const variationKey = `${mainMoveIdx}|${varIdx}`;
            
            if (!variationGroups.has(variationKey)) {
              const parentMove = moves[mainMoveIdx];
              
              if (parentMove?.variations?.[varIdx]) {
                variationGroups.set(variationKey, {
                  parentMove: parentMove,
                  parentIndex: mainMoveIdx,
                  variationIndex: varIdx,
                  variation: parentMove.variations[varIdx],
                  firstCommentMove: parentMove.variations[varIdx].find(
                    varMove => varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
                      varMove?.creator?.id === userId
                  ) || parentMove.variations[varIdx][0],
                  isNested: false,
                  fullPath: commentMove.path
                });
              }
            }
          } else {
            // This is a nested variation - need to resolve the parent path
            const lastPathSegment = pathParts[pathParts.length - 1];
            const [varIdx] = lastPathSegment.split(':').map(Number);
            const variationKey = `${variationPath}|${varIdx}`;
            
            if (!variationGroups.has(variationKey)) {
              const parentResult = getNodeAtPathInfinite(moves, variationPath);
              if (parentResult.success) {
                if (parentResult.node?.variations?.[varIdx]) {
                  variationGroups.set(variationKey, {
                    parentMove: parentResult.node,
                    parentIndex: mainMoveIdx, // Keep the original main line index for display
                    variationIndex: varIdx,
                    variation: parentResult.node.variations[varIdx],
                    firstCommentMove: parentResult.node.variations[varIdx].find(
                      varMove => varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
                        varMove?.creator?.id === userId
                    ) || parentResult.node.variations[varIdx][0],
                    isNested: true,
                    fullPath: commentMove.path,
                    parentPath: variationPath
                  });
                }
              }
            }
          }
        }
      });
      
      // Convert the map values to array
      variationsWithCommentId = Array.from(variationGroups.values());
      
     
    }

    if (variationsWithCommentId.length === 0) {
      
      return (
        <></>
      );
    }
    
    

    // For preview, show the first variation we found
    const variation = variationsWithCommentId[0];
    
    // Handle nested vs first-level variations differently
    let parentMoveId, variationMoveId;
    
    if (variation.isNested) {
      // For nested variations, the parent is not the main line
      parentMoveId = variation.parentPath;
      variationMoveId = `${variation.parentPath}|${variation.variationIndex}:0`;
    } else {
      // For first-level variations
      parentMoveId = String(variation.parentIndex);
      variationMoveId = `${variation.parentIndex}|${variation.variationIndex}:0`;
    }
    
    const firstCommentMove = variation.firstCommentMove;

    // Calculate move number for display
    let moveNum;
    if (variation.isNested) {
      // For nested variations, we need to calculate the move number differently
      // We should get the move number from the actual parent move in the nested structure
      try {
        const nodeResult = getNodeAtPathInfinite(moves, variation.parentPath);
        if (nodeResult.success && nodeResult.nodeIndex !== -1) {
          // Calculate the move number based on the position in the parent variation
          const baseNum = Math.floor(variation.parentIndex / 2) + 1;
          const variationMoveNum = Math.floor(nodeResult.nodeIndex / 2) + 1;
          moveNum = `${baseNum}.${variationMoveNum}`;
        } else {
          moveNum = '?';
        }
      } catch (error) {
        console.error('[3Moves] Error calculating nested move number:', error);
        moveNum = '?';
      }
    } else {
      // For first-level variations, calculate proper move number with color indication
      const baseNumber = Math.floor(variation.parentIndex / 2) + 1;
      const isWhiteMove = variation.parentIndex % 2 === 0;
      
      if (isWhiteMove) {
        moveNum = baseNumber; // e.g., "3" for 3. White
      } else {
        moveNum = `${baseNumber}...`; // e.g., "3..." for 3... Black
      }
    }

    

    return (
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 5,
          padding: '2px 8px',
          borderRadius: 4
        }}>
          {/* Parent/Main Line Move */}
          <button
            onClick={() => goToMove(parentMoveId)}
            style={{
              background: getMoveBackgroundColor(variation.parentMove, parentMoveId, '#666'),
              color: getMoveBackgroundColor(variation.parentMove, parentMoveId, '#666') === '#ffd700' ? 'black' : 'white',
              border: 'none',
              padding: mobileStyles.moveButton.padding,
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: mobileStyles.moveButton.fontSize,
            }}
            title="View main line move"
          >
            {moveNum}{moveNum.toString().includes('...') ? '' : '.'} {variation.parentMove.notation}
          </button>

          {/* Arrow */}
          <div style={{ color: '#808080', fontSize: '1.2rem' }}>
            {/* <i className="fa-solid fa-arrow-right"></i> */}
            →
          </div>

          {/* First Comment Move */}
          <div syle={{ position: 'relative' }}>
          <button
            onClick={() => goToMove(variationMoveId)}
            style={{
              background: getMoveBackgroundColor(firstCommentMove, variationMoveId, '#808080'),
              color: getMoveBackgroundColor(firstCommentMove, variationMoveId, '#808080') === '#ffd700' ? 'black' : 'white',
              border: 'none',
              padding: mobileStyles.moveButton.padding,
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: mobileStyles.moveButton.fontSize,
            }}
            title="View comment variation"
          >
            {firstCommentMove.notation}
          </button>

          {/* More moves indicator */}
          {variation.variation.length > 1 && (
           
           <i className="fa-solid fa-ellipsis" style={{ marginLeft: '10px', color: '#808080', fontSize: mobileStyles.smallIcon.fontSize }}></i>
        
       )}
          </div>
        </div>
        
      
    );
  } else if (type === 'COMMENTS') {
    
    const filteredMoves = filterMovesForComment(moves, userId);
    
    if (filteredMoves.length === 0) {
      return (
        <div style={{ padding: 12, background: '#252525', borderRadius: 4 }}>
          
        </div>
      );
    }
    
    // Convert comment moves to a format suitable for trio rendering
    const commentMoveButtons = filteredMoves.map((commentMove, index) => {
      return {
        notation: commentMove.notation,
        path: commentMove.path,
        creator: { id: commentMove.commentId, type: CHESSBOARD.MOVE_TYPES.COMMENT },
        isCommentMove: true,
        commentId: commentMove.commentId,
        originalIndex: index
      };
    });
    
    // Group comment moves into trios for proper display
    const commentTrios = [];
    let currentTrio = { moveNumber: null, whiteMove: null, blackMove: null };
    
    commentMoveButtons.forEach((move, index) => {
      // Calculate move number (starting from 1)
      const moveNumber = Math.floor(index / 2) + 1;
      const isWhiteMove = index % 2 === 0;
      
      if (isWhiteMove) {
        // Start a new trio for white's move
        currentTrio = {
          moveNumber: moveNumber,
          whiteMove: move,
          blackMove: null,
          whiteIndex: index,
          blackIndex: null
        };
        commentTrios.push(currentTrio);
      } else {
        // Add black's move to the current trio
        if (commentTrios.length > 0) {
          const lastTrio = commentTrios[commentTrios.length - 1];
          lastTrio.blackMove = move;
          lastTrio.blackIndex = index;
        }
      }
    });
    
    return (
      <div style={{ padding: 12, background: '#252525', borderRadius: 4, border: '2px solid #333333' }}>
        
        
        <div style={{ display: 'flex', flexDirection: 'row', gap: 20, justifyContent: 'center', alignItems: 'center', padding: '0 20px' }}>
          {(() => {
           
            
            // Find the current trio index by comparing paths instead of indices
            let currentTrioIndex = -1;
            
            // Find which trio contains the current move by comparing paths
            for (let i = 0; i < commentTrios.length; i++) {
              const trio = commentTrios[i];
              const whiteMovePath = trio.whiteMove?.path;
              const blackMovePath = trio.blackMove?.path;
              
              if ((whiteMovePath && whiteMovePath === currentMove) ||
                  (blackMovePath && blackMovePath === currentMove)) {
                currentTrioIndex = i;
                
                break;
              }
            }
            
            // If no exact match found, try to find a logical current trio based on navigation
            if (currentTrioIndex === -1) {
              // Try to find the most recently navigated trio or default to first
              
              
              // For better UX, we could implement a "sliding window" that tracks the user's navigation
              // For now, default to the first trio
              currentTrioIndex = 0;
              
              // If we have more than 2 trios and currentMove indicates we're further along,
              // try to position the window better
              if (commentTrios.length > 2) {
                // This is a placeholder for better logic - could be improved based on move order
                const moveIndex = parseInt(currentMove.split('|')[0]) || 0;
                const estimatedTrioIndex = Math.floor(moveIndex / 2);
                if (estimatedTrioIndex < commentTrios.length) {
                  currentTrioIndex = Math.max(0, Math.min(estimatedTrioIndex, commentTrios.length - 2));
                }
              }
            }
            
            
            
            // Calculate which trios to show: current and next
            const currentTrio = commentTrios[currentTrioIndex];
            const nextTrio = currentTrioIndex < commentTrios.length - 1 ? commentTrios[currentTrioIndex + 1] : null;
            
            
            
            // Check if there are moves before or after the visible range
            const hasMovesBeforeCurrentTrio = currentTrioIndex > 0;
            const hasMovesAfterNextTrio = nextTrio ? (currentTrioIndex + 1 < commentTrios.length - 1) : (currentTrioIndex < commentTrios.length - 1);
            
            return (
              <>
                {/* Left ellipsis indicator */}
                {hasMovesBeforeCurrentTrio && (
                  <button
                    onClick={() => {
                      // Navigate to FIRST comment move
                      if (filteredMoves.length > 0) {
                        goToMove(filteredMoves[0].path);
                      }
                    }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      color: '#ffcc00', 
                      fontSize: '18px',
                      fontWeight: 'bold',
                      minWidth: '20px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = '#ffdd33';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = '#ffcc00';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="Go to first comment move"
                  >
                    <i className="fa-solid fa-ellipsis" style={{ marginLeft: '5px', color: '#ff6600', fontSize: mobileStyles.smallIcon.fontSize }}></i>
                  </button>
                )}

                {/* Left Column: Current Move Pair */}
                <div style={{ flex: 1, minWidth: '150px' }}>
                  {currentTrio && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Move number */}
              <span style={{ color: '#ccc', minWidth: mobileStyles.moveNumber.minWidth, fontSize: mobileStyles.moveNumber.fontSize }}>
                        {currentTrio.moveNumber}.
              </span>
              
              {/* White move */}
                      {currentTrio.whiteMove && (
                <button
                  onClick={() => {
                            if (currentTrio.whiteMove.path) {
                              goToMove(currentTrio.whiteMove.path);
                    }
                  }}
                  style={{
                    position: 'relative',
                            background: getMoveBackgroundColor(currentTrio.whiteMove, currentTrio.whiteMove.path || `${currentTrio.whiteIndex}`, '#666'),
                            color: getMoveBackgroundColor(currentTrio.whiteMove, currentTrio.whiteMove.path || `${currentTrio.whiteIndex}`, '#666') === '#ffd700' ? 'black' : 'white',
                    padding: mobileStyles.moveButton.padding,
                    borderRadius: 4,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: mobileStyles.moveButton.fontSize,
                    minWidth: mobileStyles.moveButton.minWidth
                  }}
                          title={`Comment move: ${currentTrio.whiteMove.notation} at ${currentTrio.whiteMove.path}`}
                >
                          {currentTrio.whiteMove.notation}
                </button>
              )}
              
              {/* Black move */}
                      {currentTrio.blackMove && (
                <button
                  onClick={() => {
                            if (currentTrio.blackMove.path) {
                              goToMove(currentTrio.blackMove.path);
                    }
                  }}
                  style={{
                    position: 'relative',
                            background: getMoveBackgroundColor(currentTrio.blackMove, currentTrio.blackMove.path || `${currentTrio.blackIndex}`, '#666'),
                            color: getMoveBackgroundColor(currentTrio.blackMove, currentTrio.blackMove.path || `${currentTrio.blackIndex}`, '#666') === '#ffd700' ? 'black' : 'white',
                    padding: mobileStyles.moveButton.padding,
                    borderRadius: 4,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: mobileStyles.moveButton.fontSize,
                    minWidth: mobileStyles.moveButton.minWidth
                  }}
                          title={`Comment move: ${currentTrio.blackMove.notation} at ${currentTrio.blackMove.path}`}
                >
                          {currentTrio.blackMove.notation}
                </button>
              )}
                    </div>
                  )}
                </div>

                {/* Right Column: Next Move Pair */}
                <div style={{ flex: 1, minWidth: '150px' }}>
                  {nextTrio && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* Move number */}
                      <span style={{ color: '#999', minWidth: mobileStyles.moveNumber.minWidth, fontSize: mobileStyles.moveNumber.fontSize }}>
                        {nextTrio.moveNumber}.
                      </span>
                      
                      {/* White move */}
                      {nextTrio.whiteMove && (
                        <button
                          onClick={() => {
                            if (nextTrio.whiteMove.path) {
                              goToMove(nextTrio.whiteMove.path);
                            }
                          }}
                          style={{
                            position: 'relative',
                            background: getMoveBackgroundColor(nextTrio.whiteMove, nextTrio.whiteMove.path || `${nextTrio.whiteIndex}`, '#666'),
                            color: getMoveBackgroundColor(nextTrio.whiteMove, nextTrio.whiteMove.path || `${nextTrio.whiteIndex}`, '#666') === '#ffd700' ? 'black' : 'white',
                            padding: mobileStyles.moveButton.padding,
                            borderRadius: 4,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: mobileStyles.moveButton.fontSize,
                            minWidth: mobileStyles.moveButton.minWidth
                          }}
                          title={`Comment move: ${nextTrio.whiteMove.notation} at ${nextTrio.whiteMove.path}`}
                        >
                          {nextTrio.whiteMove.notation}
                        </button>
                      )}
                      
                      {/* Black move */}
                      {nextTrio.blackMove && (
                        <button
                          onClick={() => {
                            if (nextTrio.blackMove.path) {
                              goToMove(nextTrio.blackMove.path);
                            }
                          }}
                          style={{
                            position: 'relative',
                            background: getMoveBackgroundColor(nextTrio.blackMove, nextTrio.blackMove.path || `${nextTrio.blackIndex}`, '#666'),
                            color: getMoveBackgroundColor(nextTrio.blackMove, nextTrio.blackMove.path || `${nextTrio.blackIndex}`, '#666') === '#ffd700' ? 'black' : 'white',
                            padding: mobileStyles.moveButton.padding,
                            borderRadius: 4,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: mobileStyles.moveButton.fontSize,
                            minWidth: mobileStyles.moveButton.minWidth
                          }}
                          title={`Comment move: ${nextTrio.blackMove.notation} at ${nextTrio.blackMove.path}`}
                        >
                          {nextTrio.blackMove.notation}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Right ellipsis indicator */}
                {hasMovesAfterNextTrio && (
                  <button
                    onClick={() => {
                      // Navigate to LAST comment move
                      if (filteredMoves.length > 0) {
                        goToMove(filteredMoves[filteredMoves.length - 1].path);
                      }
                    }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      color: '#ffcc00', 
                      fontSize: '18px',
                      fontWeight: 'bold',
                      minWidth: '20px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = '#ffdd33';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = '#ffcc00';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="Go to last comment move"
                  >
                    ...
                  </button>
                )}
              </>
            );
          })()}
        </div>
              
        {/* User indicator with navigation arrows - moved below the columns */}
        <div style={{ 
          marginTop: '25px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px'
        }}>
          {/* Left Arrow - Previous Move */}
          <button
            onClick={() => {
              // Find current move in the filteredMoves array and go to previous
              const currentIndex = filteredMoves.findIndex(move => move.path === currentMove);
              if (currentIndex > 0) {
                const prevMove = filteredMoves[currentIndex - 1];
                goToMove(prevMove.path);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffcc00',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '5px',
              borderRadius: '3px',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255, 204, 0, 0.2)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Previous comment move"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>

          {/* Temporary Indicator with Controls */}
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            {/* Trash icon (Delete Variation) */}
            {temporaryControls && (
              <div
                onClick={temporaryControls.onDelete}
                style={{
                  color: '#ff3366',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  padding: '3px'
                }}
                title="Delete this variation"
                onMouseOver={(e) => {
                  e.currentTarget.style.color = '#ff6699';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = '#ff3366';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <i className="fa-solid fa-trash"></i>
              </div>
            )}

              <span style={{ 
              color: '#ffcc00',
                fontSize: '0.8em',
                padding: '2px 6px',
              background: 'rgba(255, 204, 0, 0.2)',
              borderRadius: 3
              }}>
              Temporary
              </span>

            {/* Undo icon (Undo Move) */}
            {temporaryControls && (
              <div
                onClick={temporaryControls.onUndo}
                style={{
                  color: '#3066be',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  padding: '3px'
                }}
                title="Undo last move"
                onMouseOver={(e) => {
                  e.currentTarget.style.color = '#4488ee';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = '#3066be';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <i className="fa-solid fa-scissors"></i>
            </div>
            )}
          </div>

          {/* Right Arrow - Next Move */}
          <button
            onClick={() => {
              // Find current move in the filteredMoves array and go to next
              const currentIndex = filteredMoves.findIndex(move => move.path === currentMove);
              if (currentIndex >= 0 && currentIndex < filteredMoves.length - 1) {
                const nextMove = filteredMoves[currentIndex + 1];
                goToMove(nextMove.path);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffcc00',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '5px',
              borderRadius: '3px',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255, 204, 0, 0.2)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Next comment move"
          >
            <i className="fa-solid fa-arrow-right"></i>
          </button>
        </div>
      </div>
    );
  } else if (type === 'COMMENT_FULL') {
    
    
    // Get all comment moves regardless of user ID
    const allCommentMoves = findCommentMovesInVariations(moves);
    
    if (allCommentMoves.length === 0) {
      return (
        <div style={{ padding: 12, background: '#252525', borderRadius: 4 }}>
          <h4 style={{ color: '#ccc', margin: '0 0 12px 0', fontSize: 16 }}>Comment Full Mode</h4>
          <div style={{ color: '#888' }}>No comment moves found</div>
        </div>
      );
    }
    
    // Convert comment moves to a format suitable for trio rendering
    const commentMoveButtons = allCommentMoves.map((commentMove, index) => {
      return {
        notation: commentMove.notation,
        path: commentMove.path,
        creator: { id: commentMove.commentId, type: CHESSBOARD.MOVE_TYPES.COMMENT },
        isCommentMove: true,
        commentId: commentMove.commentId,
        originalIndex: index
      };
    });
    
    // Group comment moves into trios for proper display
    const commentTrios = [];
    let currentTrio = { moveNumber: null, whiteMove: null, blackMove: null };
    
    commentMoveButtons.forEach((move, index) => {
      // Calculate move number (starting from 1)
      const moveNumber = Math.floor(index / 2) + 1;
      const isWhiteMove = index % 2 === 0;
      
      if (isWhiteMove) {
        // Start a new trio for white's move
        currentTrio = {
          moveNumber: moveNumber,
          whiteMove: move,
          blackMove: null,
          whiteIndex: index,
          blackIndex: null
        };
        commentTrios.push(currentTrio);
      } else {
        // Add black's move to the current trio
        if (commentTrios.length > 0) {
          const lastTrio = commentTrios[commentTrios.length - 1];
          lastTrio.blackMove = move;
          lastTrio.blackIndex = index;
        }
      }
    });
    
    return (
      <div style={{}}>
      
        
        
      </div>
    );
  }

  // If this is puzzle mode, use the puzzle-specific component
  if (isPuzzle) {
    return (
      <PuzzleMoves 
        maxMoveIndex={maxMoveIndex} 
        revealedMoves={revealedMoves}
        moves={moves}
        currentMove={currentMove}
        goToMove={goToMove}
        initialFEN={initialFEN}
      />
    );
  }

  // Otherwise, use the regular moves component
  if (!moves || moves.length === 0) return <div></div>;

  /**
   * Determines if a move has variations
   * @param {Object} move - Move object
   * @returns {boolean} - True if has variations
   */
  const hasVariations = (move) => {
    return move?.variations && move.variations.length > 0;
  };

  /**
   * Logs the variation structure for a move with variations
   * @param {Object} move - Move object to check
   */
  const logVariationStructure = (move) => {
    if (!hasVariations(move)) return;
    
    // 
    move.variations.forEach((variation, index) => {
      // console.log(`Variation ${index}:`, JSON.stringify(variation.map(vm => ({
      //   notation: vm.notation,
      //   moveNum: vm.moveNum,
      //   content: vm.content,
      //   arrows: vm.arrows,
      //   opening: vm.opening,
      //   analysis: vm.analysis ? {
      //     classification: vm.analysis.classification,
      //     depth: vm.analysis.depth
      //   } : null
      // })), null, 2));
    });
  };

  /**
   * Enhanced path resolution with infinite nested variation support
   * @param {Array} moves - The full move list
   * @param {Array} pathParts - Path parts split by '|'
   * @returns {Object} - Object with moves array, focus index, and parent path
   */
  const resolvePath = (moves, pathParts) => {
    if (!pathParts || pathParts.length === 0) {
      // FILTER COMMENT MOVES FROM MAIN LINE for DEFAULT type
      const filteredMoves = type === 'DEFAULT' 
        ? moves.filter(move => !isCommentMove(move))
        : moves;
      
      return { moves: filteredMoves, focus: -1, from: null };
    }
    
    // Handle starting position (both plain -1 and -1 variations)
    if (pathParts[0] === '-1') {
      if (pathParts.length === 1) {
        // Plain starting position
        const filteredMoves = type === 'DEFAULT' 
          ? moves.filter(move => !isCommentMove(move))
          : moves;
        
        return { moves: filteredMoves, focus: -1, from: null };
      } else {
        // Starting position variation (-1|0:0, -1|0:1, etc.)
        const pathString = pathParts.join('|');
        
        // Parse the variation part
        const variationPart = pathParts[1];
        if (variationPart && variationPart.includes(':')) {
          const [varIdx, moveIdx] = variationPart.split(':').map(Number);
          
          // Get starting position variations from state
          if (state?.startPosition?.variations && varIdx < state.startPosition.variations.length) {
            const variation = state.startPosition.variations[varIdx];
            if (variation && Array.isArray(variation)) {
              return {
                moves: variation,
                focus: moveIdx,
                from: '-1',
                isStartingPositionVariation: true
              };
            }
          }
        }
        
        // Fallback if variation not found
        return { moves: [], focus: -1, from: null };
      }
    }
    
    // Use the enhanced path utilities for infinite nesting support
    const pathString = pathParts.join('|');
    const result = getNodeAtPathInfinite(moves, pathString);
    
    if (!result.success) {
      console.warn('[3Moves.jsx] resolvePath: Failed to resolve path:', pathString, result.error);
      // Fallback to filtered main line moves
      const filteredMoves = type === 'DEFAULT' 
        ? moves.filter(move => !isCommentMove(move))
        : moves;
      return { moves: filteredMoves, focus: 0, from: null };
    }
    
    // Determine parent path
    const parentPath = result.segments && result.segments.length > 1 
      ? getParentPath(pathString) 
      : null;
    
    // For main line positions, apply comment filtering if needed
    const lastSegment = result.segments[result.segments.length - 1];
    const isMainLineOnly = result.segments.length === 1 && lastSegment.type === 'main';
    
    let targetMoves = result.nodeArray;
    if (isMainLineOnly && type === 'DEFAULT') {
      targetMoves = targetMoves.filter(move => !isCommentMove(move));
    }
    
    return {
      moves: targetMoves,
      focus: result.nodeIndex,
      from: parentPath,
      // Additional metadata for enhanced functionality
      depth: getPathDepth(pathString),
      isVariation: isInVariationPath(pathString),
      segments: result.segments
    };
  };

  /**
   * Helper to determine the starting color at a particular node in the move tree
   * Uses chess.js to calculate the actual starting color based on FEN
   * @param {string} path - The path to the position
   * @returns {string} - 'w' for white to move, 'b' for black to move
   */
  const getStartColor = (path) => {
    if (!path || path === '-1') {
      // For the initial position, check if we have a custom FEN
      if (initialFEN) {
        try {
          const chess = new Chess(initialFEN);
          return chess.turn();
        } catch (e) {
          console.error('Invalid initial FEN:', initialFEN);
          return 'w';
        }
      }
      return 'w'; // Default starting position is white to move
    }
    
    // Initialize a new chess instance with initial FEN if provided
    const chess = initialFEN ? new Chess(initialFEN) : new Chess();
    
    // Calculate the position up to the parent move
    const pathParts = path.split('|');
    const mainLine = parseInt(pathParts[0], 10);
    
    // Apply main line moves up to the parent move
    for (let i = 0; i <= mainLine && i < moves.length; i++) {
      if (moves[i]?.notation) {
        try {
          chess.move(moves[i].notation);
        } catch (e) {
          console.error(`Invalid move ${moves[i].notation} at index ${i}`, e);
        }
      }
    }
    
    // If we have variation path, follow it
    if (pathParts.length > 1) {
      let currentMoves = moves;
      let currentNode = moves[mainLine];
      
      for (let i = 1; i < pathParts.length; i++) {
        const [varIdx, moveIdx] = pathParts[i].split(':').map(Number);
        
        if (!currentNode?.variations?.[varIdx]) break;
        
        // Apply moves in this variation
        const variation = currentNode.variations[varIdx];
        for (let j = 0; j <= moveIdx && j < variation.length; j++) {
          if (variation[j]?.notation) {
            try {
              chess.move(variation[j].notation);
            } catch (e) {
              console.error(`Invalid variation move ${variation[j].notation}`, e);
            }
          }
        }
        
        if (moveIdx < variation.length - 1) {
          currentNode = variation[moveIdx];
          currentMoves = variation;
        } else break;
      }
    }
    
    // Determine whose turn it is from the FEN
    return chess.turn();
  };

  /**
   * Gets the color that starts a variation based on whose turn it is
   * @param {string} path - Path to position where variation branches
   * @param {number} moveIndex - Index of the move where variation branches
   * @returns {string} - 'w' or 'b'
   */
  const getVariationStartColor = (path, moveIndex) => {
    // Use the path utilities to get the position accurately
    const nodeResult = getNodeAtPathInfinite(moves, path);
    if (!nodeResult.success) {
      return 'w'; // Default to white if we can't resolve the path
    }
    
    // Calculate the position FEN at the path using the resolved sequence
    const chess = initialFEN ? new Chess(initialFEN) : new Chess();
    
    // Apply all moves in the sequence to get to the current position
    const moveSequence = getMoveSequenceToPath(moves, path);
    
    for (const move of moveSequence) {
      if (move?.notation) {
        try {
          chess.move(move.notation);
        } catch (e) {
          console.error(`Invalid move in sequence: ${move.notation}`, e);
        }
      }
    }
    
    // The color to move at this position is opposite of what the variation starts with
    const positionColor = chess.turn();
    return positionColor === 'w' ? 'b' : 'w';
  };

  /**
   * Calculate move number at a specific position
   * @param {string} path - The path to the position
   * @param {number} index - Current move index
   * @returns {number} - The move number
   */
  const calculateMoveNumber = (path, index) => {
    if (!path) return 1;
    
    const parts = path.split('|');
    const lastPart = parts[parts.length - 1];
    
    if (lastPart.includes(':')) {
      const [varIdx, moveIdx] = lastPart.split(':').map(Number);
      const parentPath = parts.slice(0, -1).join('|');
      
      // If we have a parent, recursively calculate from there
      if (parentPath) {
        const parentMoveNumber = calculateMoveNumber(parentPath, parseInt(parts[parts.length - 2], 10));
        const parentColor = getStartColor(parentPath);
        
        // Adjust based on color and position
        if (parentColor === 'w') {
          return parentMoveNumber + Math.floor(moveIdx / 2);
        } else {
          return parentMoveNumber + Math.floor((moveIdx + 1) / 2);
        }
      }
    }
    
    // Simple case - main line
    return Math.floor(parseInt(lastPart, 10) / 2) + 1;
  };

  /**
   * Groups moves into trios for display, handling black-first positions correctly
   * @param {Array} moves - Array of moves
   * @param {string} startWith - Starting color
   * @returns {Array} - Array of trios
   */
  const getTrios = (moves, startWith = 'w', startingMoveNumber = 1) => {
    if (!moves || moves.length === 0) return [];
    
   
    
    const trios = [];
    let moveNumber = startingMoveNumber; // Use provided starting move number
    
    // If black moves first, create an empty first move
    if (startWith === 'b') {
      trios.push({
        moveNumber,
        whiteMove: null,
        whiteIndex: null,
        blackMove: moves[0],
        blackIndex: 0,
        blackFirst: true // Mark as a black-first move
      });
      moveNumber++; // Increment to move 2
      
      // Process the rest of the moves
      for (let i = 1; i < moves.length; i++) {
        const isWhite = i % 2 === 1; // After first black move, odd indices are white
        
        if (isWhite) {
          // White's move - start a new trio
          trios.push({
            moveNumber,
            whiteMove: moves[i],
            whiteIndex: i,
            blackMove: null,
            blackIndex: null
          });
        } else {
          // Black's move - complete the current trio
          if (trios.length > 0) {
            const lastTrio = trios[trios.length - 1];
            lastTrio.blackMove = moves[i];
            lastTrio.blackIndex = i;
            moveNumber++; // Increment move number after black's move
          }
        }
      }
    } 
    else {
      // Standard case - white moves first
      for (let i = 0; i < moves.length; i++) {
        const isWhite = i % 2 === 0;
        
        if (isWhite) {
          // White's move - start a new trio
          trios.push({
            moveNumber,
            whiteMove: moves[i],
            whiteIndex: i,
            blackMove: null,
            blackIndex: null
          });
        } else {
          // Black's move - complete the current trio
          if (trios.length > 0) {
            const lastTrio = trios[trios.length - 1];
            lastTrio.blackMove = moves[i];
            lastTrio.blackIndex = i;
            moveNumber++; // Increment move number after black's move
          }
        }
      }
    }
    
    return trios;
  };

  /**
   * Finds the trio containing a specific move
   * @param {Array} moves - Array of moves
   * @param {number} focusIndex - Index of the move to focus
   * @param {string} startColor - Starting color for this variation
   * @returns {Object} - Contains trios array and focused trio
   */
  const focusTrio = (moves, focusIndex, startColor, currentPath = null) => {
    // Calculate the starting move number for variations
    let startingMoveNumber = 1;
    
    if (currentPath && currentPath.includes('|')) {
      // This is a variation - calculate the starting move number based on the parent move
      const pathParts = currentPath.split('|');
      const parentMoveIndex = parseInt(pathParts[0], 10);
      
      // The variation starts AFTER the parent move
      // So if parent is move 5 (index 4), the variation starts at move 6
      // But we need to account for which color moves first in the variation
      
      // Parent move index tells us what move was just played
      // The variation starts with the NEXT player to move
      const parentMoveNumber = Math.floor(parentMoveIndex / 2) + 1;
      const parentWasWhite = parentMoveIndex % 2 === 0;
      
      if (parentWasWhite) {
        // Parent was white's move, so variation starts with black
        startingMoveNumber = parentMoveNumber;
        startColor = 'b';
      } else {
        // Parent was black's move, so variation starts with white's next move
        startingMoveNumber = parentMoveNumber + 1;
        startColor = 'w';
      }
    }
    
    const trios = getTrios(moves, startColor, startingMoveNumber);
    
    if (trios.length === 0) {
      return { trios, current: null, prev: null, next: null, index: -1 };
    }
    
    // Find which trio contains our focused move
    const idx = trios.findIndex(trio => 
      (trio.whiteIndex === focusIndex) || (trio.blackIndex === focusIndex)
    );
    
    if (idx === -1) {
      return { trios, current: trios[0], prev: null, next: trios[1], index: 0 };
    }
    
    return { 
      trios, 
      current: trios[idx], 
      next: idx + 1 < trios.length ? trios[idx + 1] : null, 
      prev: idx > 0 ? trios[idx - 1] : null, 
      index: idx 
    };
  };

  /**
   * Renders a move button with proper styling and click behavior
   * @param {Object} move - The move object with notation
   * @param {string} id - The full path ID for this move
   * @param {string} highlightColor - Background color for the button
   * @param {boolean} hideVariationArrow - If true, hides the variation arrow even if move has variations
   */
  const renderMoveButton = (move, id, highlightColor = '#444', hideVariationArrow = false) => {
    if (!move) return null;
    
    const safeId = id || '';
    
    // Check if this is a comment move and log it (only once per move)
    if (isCommentMove(move) && !move._loggedAsRendered) {
      
      move._loggedAsRendered = true; // Mark as logged to prevent spam
    }
    
    const backgroundColor = getMoveBackgroundColor(move, safeId, highlightColor);
    
    // Determine text color based on background color
    const textColor = backgroundColor === '#ffd700' ? 'black' : 'white';
    
    return (
      <button
        key={safeId}
        onClick={() => {
          // 
          
          // Prevent clicking on the current move to avoid rendering issues
          if (isCurrentForStyling(safeId)) {
            // 
            return;
          }
          
          goToMove(safeId);
          
          // If this move has variations, log their structure
          if (hasVariations(move)) {
            logVariationStructure(move);
          }
        }}
        style={{
          position: 'relative',
          background: backgroundColor,
          color: textColor,
          padding: mobileStyles.moveButton.padding,
          borderRadius: 4,
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.95rem',
          minWidth: mobileStyles.moveButton.minWidth
        }}
        title={move.content ? move.content : (isCommentMove(move) ? `Comment move by user ${move.creator.id}` : '')}
      >
        {move.notation}
        {/* {isCommentMove(move) && (
          <span style={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#ff0000',
            border: '1px solid white'
          }}></span>
        )} */}
        {hasVariations(move) && !hideVariationArrow && !isStartPositionVariation && (
          <i className="fa-solid fa-code-branch" 
          onClick={(e) => {
            e.stopPropagation();
            // Navigate to first variation of this move (like down arrow behavior)
            const moveIndex = parseInt(safeId, 10);
            if (!isNaN(moveIndex) && moves[moveIndex]?.variations?.length > 0) {
              // Navigate to first variation
              goToMove(`${moveIndex}|0:0`);
            }
          }}
          style={{
            position: 'absolute',
            bottom: -10,
            right: '-25%',
            width: 8,
            height: 8,
            color: getVariationArrowColor(move, safeId),
            transform: 'rotate(90deg)', // 90 degrees clockwise rotation
            cursor: 'pointer' // Make it clear it's clickable
          }}
          title="Enter first variation"
          ></i>
        )}
        {isStarred(move, safeId) && (
          // <span style={{
          //   position: 'absolute',
          //   bottom: -2,
          //   right: -2,
          //   width: 6,
          //   height: 6,
          //   borderRadius: '50%',
          //   background: '#ffcc00'
          // }}></span>
          <i className="fa-solid fa-star" 
          // class="fa-solid fa-rotate-180 fa-arrows-split-up-and-left" 
          style={{
            position: 'absolute',
            top: -8,
            left: -8,
            width: 8,
            height: 8,
            color: 'gold'
          }}
          ></i>
        )}
        {move.content && (
          <i className="fa-solid fa-a" 
          style={{
            position: 'absolute',
            top: -9,
            right: -8,
            
            color: 'black',
            fontWeight: 'bold',
            backgroundColor: '#58b047',
            width: 18,
            height: 18,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
          }}
          ></i>
        )}
        {/* {move.arrows && move.arrows.length > 0 && (
          <span style={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: '#00ccff'
          }}></span>
        )} */}
        {/* {move.analysis && move.analysis.classification && (
          <span style={{
            position: 'absolute',
            top: -2,
            left: -2,
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: move.analysis.classification === 'good' ? '#3DD66F' :
                      move.analysis.classification === 'inaccuracy' ? '#F9A62B' :
                      move.analysis.classification === 'mistake' ? '#DB843D' :
                      move.analysis.classification === 'blunder' ? '#E54B4B' : '#999'
          }}></span>
        )} */}
      </button>
    );
  };
  

  /**
   * Renders a trio (move number, white move, black move) with proper notation
   * @param {Object} trio - Trio object
   * @param {string} baseId - Base move ID for this trio
   * @param {string} prefixColor - Color for the move number
   * @param {boolean} forceMainLineIds - If true, always use main line IDs regardless of current variation state
   * @param {string} lineLabel - Optional label to display before the move number (e.g., "(Main Line)", "(Variation)")
   * @param {boolean} showTestBetweenMoves - If true, shows "TEST" between white and black moves
   * @param {string} dynamicColor - Optional color override for arrows and labels
   * @param {boolean} isParentContext - If true, hides variation arrows for the second move
   * @returns {JSX.Element} - Rendered trio
   */
  const renderTrio = (trio, baseId, prefixColor = '#ccc', forceMainLineIds = false, lineLabel = null, showTestBetweenMoves = false, dynamicColor = null, isParentContext = false) => {
    if (!trio) return null;
    
    // Ensure baseId is a string to prevent errors
    const safeBaseId = baseId || '';
    
    // 
    
    // Construct proper IDs for white and black moves
    let whiteId = null;
    let blackId = null;
    
    // Check if we're currently in a variation to determine ID format
    if (currentMove.includes('|') && !forceMainLineIds) {
      // We're in a variation - need to construct variation-style IDs
      const currentParts = currentMove.split('|');
      
      // For nested variations, we need to preserve the full path structure
      // The baseId should contain the parent path up to the current variation level
      let pathPrefix = '';
      if (currentParts.length >= 2) {
        // Get the current variation's base path by finding the last colon
        const fullPath = currentMove;
        const lastColonIndex = fullPath.lastIndexOf(':');
        pathPrefix = fullPath.substring(0, lastColonIndex + 1);
      }
      
      // For variation moves, construct IDs using the proper path prefix
      whiteId = trio.whiteIndex !== null 
        ? `${pathPrefix}${trio.whiteIndex}`
        : null;
        
      blackId = trio.blackIndex !== null 
        ? `${pathPrefix}${trio.blackIndex}`
        : null;
        
      
    } else {
      // We're in main line OR forced to use main line IDs - use simple number indices
      whiteId = trio.whiteIndex !== null ? String(trio.whiteIndex) : null;
      blackId = trio.blackIndex !== null ? String(trio.blackIndex) : null;
      
      
    }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Line label (e.g., "(Main Line)" or "(Variation)") */}
        {lineLabel && (
          <span style={{ color: prefixColor, fontSize: '0.9rem', fontWeight: 'bold' }}>
            {lineLabel}
          </span>
        )}
        
        {/* If it's a black-first move, display differently */}
        {trio.blackFirst ? (
          <>
            <span style={{ color: prefixColor, minWidth: mobileStyles.moveNumber.minWidth }}>{trio.moveNumber}...</span>
            {trio.blackMove && renderMoveButton(trio.blackMove, blackId, '#666')}
          </>
        ) : (
          <>
            <span style={{ color: prefixColor, minWidth: mobileStyles.moveNumber.minWidth }}>{trio.moveNumber}.</span>
            {trio.whiteMove && renderMoveButton(trio.whiteMove, whiteId, '#666')}
            
            {/* TEST text between white and black moves if requested */}
            {showTestBetweenMoves && trio.whiteMove && trio.blackMove && (
              <span style={{ color: dynamicColor || '#ff6600', fontSize: '1.5rem', fontWeight: 'bold' }}>
                →
              </span>
            )}
            
            {trio.blackMove && renderMoveButton(trio.blackMove, blackId, '#666', isParentContext)}
          </>
        )}
      </div>
    );
  };

  /**
   * Renders a trio in a variation preview
   * @param {Object} trio - Trio object
   * @param {string} baseId - Base ID for moves
   * @param {string} textColor - Color for move numbers
   * @returns {JSX.Element} - Rendered trio
   */
  const renderVariationTrio = (trio, baseId, textColor = '#999') => {
    if (!trio) return null;
    
    // Ensure baseId is a string
    const safeBaseId = baseId || '';
    
    // 
    
    // Construct IDs for white and black moves
    let whiteId = null;
    let blackId = null;
    
    // For variations, ensure we have the proper format: path|variationIdx:moveIdx
    whiteId = trio.whiteIndex !== null 
      ? `${safeBaseId}${trio.whiteIndex}`
      : null;
      
    blackId = trio.blackIndex !== null 
      ? `${safeBaseId}${trio.blackIndex}`
      : null;
    
    
    
    // Different rendering for black-first moves
    if (trio.blackFirst) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: textColor, minWidth: mobileStyles.moveNumber.minWidth }}>{trio.moveNumber}...</span>
          {trio.blackMove && renderMoveButton(trio.blackMove, blackId, '#555')}
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: textColor, minWidth: mobileStyles.moveNumber.minWidth }}>{trio.moveNumber}.</span>
          {trio.whiteMove && renderMoveButton(trio.whiteMove, whiteId, '#555')}
          {trio.blackMove && renderMoveButton(trio.blackMove, blackId, '#555')}
        </div>
      );
    }
  };

  /**
   * Renders variation previews for the current move
   * @returns {JSX.Element} - Rendered variation previews
   */
  const renderVariationPreview = () => {
    if (!moves || moves.length === 0) return null;

    // Get current move information to determine if we should show variations
    const getCurrentMoveInfo = () => {
      if (currentMove === '-1') {
        return { isStart: true, parentIndex: -1, inVariation: false, currentVariationIndex: -1 };
      }

      if (currentMove.includes('|')) {
        // We're in a variation - extract parent move index and variation index
        const parts = currentMove.split('|');
        const parentIndex = parseInt(parts[0], 10);
        const [variationIndex] = parts[1].split(':').map(Number);
        return { isStart: false, parentIndex, inVariation: true, currentVariationIndex: variationIndex };
      } else {
        // We're on main line
        const parentIndex = parseInt(currentMove, 10);
        return { isStart: false, parentIndex, inVariation: false, currentVariationIndex: -1 };
      }
    };

    const currentMoveInfo = getCurrentMoveInfo();
    
    // ENHANCED: Handle start position variations
    if (currentMoveInfo.isStart) {
      // Check if start position has variations
      if (!state?.startPosition?.variations || state.startPosition.variations.length === 0) {
        return null;
      }
      
      const variationsToShow = [];
      
      // Process start position variations (limit to exactly 2 variations total for two columns)
      state.startPosition.variations.forEach((variation, varIndex) => {
        if (variation.length > 0) {
          // Check if this variation contains comment moves
          const hasCommentMoves = variation.some(varMove => 
            varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
          );
          
          // Determine variation type for ordering
          let variationType = 'original';
          if (hasCommentMoves) {
            const isAuthorComment = variation.some(varMove => 
              varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
              varMove?.creator?.id === userId
            );
            variationType = isAuthorComment ? 'authorComment' : 'userComment';
          }
          
          variationsToShow.push({
            parentMove: null, // Start position has no parent move
            parentIndex: -1,
            variationIndex: varIndex,
            variation: variation.slice(0, 4), // Limit to 2 trios (4 moves max)
            firstMove: variation[0],
            hasCommentMoves: hasCommentMoves,
            variationType: variationType,
            isNested: false,
            isStartPositionVariation: true
          });
        }
      });
      
      if (variationsToShow.length === 0) return null;
      
      // Sort by priority: original → authorComment → userComment
      const sortOrder = { 'original': 0, 'authorComment': 1, 'userComment': 2 };
      variationsToShow.sort((a, b) => sortOrder[a.variationType] - sortOrder[b.variationType]);
      
      // LIMIT TO EXACTLY 2 VARIATIONS (for two columns) - same as traditional variations
      const limitedVariations = variationsToShow.slice(0, 2);
      
      // Split the limited variations into columns
      const firstColumnVariations = limitedVariations.slice(0, 1);
      const secondColumnVariations = limitedVariations.slice(1, 2);
      
      return (
        <div style={{ marginTop: 16, marginLeft: 8, background: '', borderRadius: 4 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            
            {/* First Column: First Starting Position Variation */}
            {firstColumnVariations.length > 0 && (
              <div style={{ flex: 1 }}>
                <h4 style={{ 
                  color: 'gold', 
                  margin: '0 0 12px 0', 
                  fontSize: 14, 
                  width: '200px',
                  paddingBottom: '5px',
                  borderBottom: '2px solid gold'
                }}>
                  <i className="fa-solid fa-circle-user" style={{ color: 'gold', marginRight: 4 }}></i>
                  Start Variations:
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {firstColumnVariations.map((varInfo, index) => {
                    const variationMoveId = `-1|${varInfo.variationIndex}:0`;
                    
                    return (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#ccc', minWidth: mobileStyles.moveNumber.minWidth, fontSize: mobileStyles.moveNumber.fontSize }}>
                          1.
                        </span>
                        
                        {/* Start position indicator */}
                        <button
                          onClick={() => goToMove('-1')}
                          style={{
                            background: '#666',
                            color: 'white',
                            padding: mobileStyles.moveButton.padding,
                            borderRadius: 4,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: mobileStyles.moveButton.fontSize,
                            minWidth: mobileStyles.moveButton.minWidth
                          }}
                          title="Go to start position"
                        >
                          ⧫
                        </button>
                        
                        {/* Arrow separator */}
                        <span style={{ color: 'gold', fontSize: '1.2rem' }}>→</span>
                        
                        {/* First variation move button */}
                        <button
                          onClick={() => goToMove(variationMoveId)}
                          style={{
                            background: 'gold',
                            color: 'black',
                            padding: mobileStyles.moveButton.padding,
                            borderRadius: 4,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: mobileStyles.moveButton.fontSize,
                            minWidth: mobileStyles.moveButton.minWidth
                          }}
                          title={`Go to variation move: ${varInfo.firstMove.notation}`}
                        >
                          {varInfo.firstMove.notation}
                        </button>
                        
                        {/* Show "..." if there are more moves in the variation */}
                        {varInfo.variation.length > 1 && (
                          <i className="fa-solid fa-ellipsis" style={{ marginLeft: '5px', color: 'gold' }}></i>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Second Column: Second Starting Position Variation */}
            {secondColumnVariations.length > 0 && (
              <div style={{ flex: 1 }}>
                <h4 style={{ 
                  color: secondColumnVariations[0].hasCommentMoves ? '#ff6600' : 'gold', 
                  margin: '0 0 12px 0', 
                  fontSize: 14, 
                  width: '200px',
                  paddingBottom: '5px',
                  borderBottom: `2px solid ${secondColumnVariations[0].hasCommentMoves ? '#ff6600' : 'gold'}`
                }}>
                  <i className={`fa-solid ${secondColumnVariations[0].hasCommentMoves ? 'fa-comments' : 'fa-circle-user'}`} style={{ color: secondColumnVariations[0].hasCommentMoves ? '#ff6600' : 'gold', marginRight: 4 }}></i>
                  {secondColumnVariations[0].hasCommentMoves ? 'Start Comment Variations:' : 'Start Variations:'}
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {secondColumnVariations.map((varInfo, index) => {
                    const variationMoveId = `-1|${varInfo.variationIndex}:0`;
                    
                    return (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#ccc', minWidth: mobileStyles.moveNumber.minWidth, fontSize: mobileStyles.moveNumber.fontSize }}>
                          1.
                        </span>
                        
                        {/* Start position indicator */}
                        <button
                          onClick={() => goToMove('-1')}
                          style={{
                            background: '#666',
                            color: 'white',
                            padding: mobileStyles.moveButton.padding,
                            borderRadius: 4,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: mobileStyles.moveButton.fontSize,
                            minWidth: mobileStyles.moveButton.minWidth
                          }}
                          title="Go to start position"
                        >
                          ⧫
                        </button>
                        
                        {/* Arrow separator */}
                        <span style={{ color: varInfo.hasCommentMoves ? '#ff6600' : 'gold', fontSize: '1.2rem' }}>→</span>
                        
                        {/* First variation move button */}
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={() => goToMove(variationMoveId)}
                            style={{
                              background: varInfo.hasCommentMoves ? '#ff6600' : 'gold',
                              color: varInfo.hasCommentMoves ? 'white' : 'black',
                              padding: mobileStyles.moveButton.padding,
                              borderRadius: 4,
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: mobileStyles.moveButton.fontSize,
                              minWidth: mobileStyles.moveButton.minWidth
                            }}
                            title={`Go to ${varInfo.hasCommentMoves ? 'comment' : ''} variation: ${varInfo.firstMove.notation}`}
                          >
                            {varInfo.firstMove.notation}
                          </button>
                        </div>
                        
                        {/* Show "..." if there are more moves in the variation */}
                        {varInfo.variation.length > 1 && (
                          <i className="fa-solid fa-ellipsis" style={{ marginLeft: '5px', color: varInfo.hasCommentMoves ? '#ff6600' : 'gold', fontSize: mobileStyles.smallIcon.fontSize }}></i>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    const variationsToShow = [];
    const nestedVariationsToShow = [];

    // PART 1: Show variations for main line parent (existing logic) + START POSITION VARIATIONS
    const targetParentIndex = currentMoveInfo.parentIndex;
    
    // ENHANCED: Handle start position variations when we're IN a start position variation
    if (targetParentIndex === -1 && currentMoveInfo.inVariation) {
      // We're in a start position variation, show sibling start position variations
      if (state?.startPosition?.variations && state.startPosition.variations.length > 0) {
        state.startPosition.variations.forEach((variation, varIndex) => {
          if (variation.length > 0) {
            // HIDE THE CURRENTLY ACTIVE VARIATION FROM PREVIEW
            if (varIndex === currentMoveInfo.currentVariationIndex) {
              return; // Skip the current variation
            }
            
            // Check if this variation contains comment moves
            const hasCommentMoves = variation.some(varMove => 
              varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
            );
            
            // Determine variation type for ordering
            let variationType = 'original';
            if (hasCommentMoves) {
              const isAuthorComment = variation.some(varMove => 
                varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
                varMove?.creator?.id === userId
              );
              variationType = isAuthorComment ? 'authorComment' : 'userComment';
            }
            
            variationsToShow.push({
              parentMove: null, // Start position has no parent move
              parentIndex: -1,
              variationIndex: varIndex,
              variation: variation.slice(0, 4), // Limit to 2 trios (4 moves max)
              firstMove: variation[0],
              hasCommentMoves: hasCommentMoves,
              variationType: variationType,
              isNested: false,
              isStartPositionVariation: true
            });
          }
        });
      }
    } else if (targetParentIndex >= 0 && targetParentIndex < moves.length) {
      // Regular main line variations
      const parentMove = moves[targetParentIndex];
      
      if (parentMove.variations && parentMove.variations.length > 0) {
        parentMove.variations.forEach((variation, varIndex) => {
          if (variation.length > 0) {
            // HIDE THE CURRENTLY ACTIVE VARIATION FROM PREVIEW
            // If we're currently in a variation and this is the same variation, skip it
            if (currentMoveInfo.inVariation && varIndex === currentMoveInfo.currentVariationIndex) {
              
              return; // Skip this variation - don't add it to variationsToShow
            }
            
            // Check if this variation contains comment moves
            const hasCommentMoves = variation.some(varMove => 
              varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
            );
            
            // Determine variation type for ordering
            let variationType = 'original';
            if (hasCommentMoves) {
              // Check if it's by the post author (you'll need to pass postAuthorId as prop)
              const isAuthorComment = variation.some(varMove => 
                varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
                varMove?.creator?.id === userId // Assuming userId represents post author when appropriate
              );
              variationType = isAuthorComment ? 'authorComment' : 'userComment';
            }
            
            variationsToShow.push({
              parentMove: parentMove,
              parentIndex: targetParentIndex,
              variationIndex: varIndex,
              variation: variation,
              firstMove: variation[0],
              hasCommentMoves: hasCommentMoves,
              variationType: variationType,
              isNested: false
            });
          }
        });
      }
    }

    // PART 2: NEW - Show nested variations if we're on a move that has child variations
    // Only show nested variations if we're NOT already showing the parent's variations above
    try {
      const currentNodeResult = getNodeAtPathInfinite(moves, currentMove);
      if (currentNodeResult.success && currentNodeResult.node?.variations?.length > 0) {
        
        
        // Only show these as "nested" if they're truly nested (not the same as parent variations)
        // If we're in a variation, we only want to show child variations, not sibling variations
        const shouldShowAsNested = currentMove.includes('|'); // We're in a variation, so these are truly nested
        
        if (shouldShowAsNested) {
          currentNodeResult.node.variations.forEach((variation, varIndex) => {
            if (variation.length > 0) {
              // Check if this variation contains comment moves
              const hasCommentMoves = variation.some(varMove => 
                varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
              );
              
              // Determine variation type for ordering
              let variationType = 'original';
              if (hasCommentMoves) {
                const isAuthorComment = variation.some(varMove => 
                  varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
                  varMove?.creator?.id === userId
                );
                variationType = isAuthorComment ? 'authorComment' : 'userComment';
              }
              
              nestedVariationsToShow.push({
                parentMove: currentNodeResult.node,
                parentPath: currentMove, // This is the current move path
                variationIndex: varIndex,
                variation: variation,
                firstMove: variation[0],
                hasCommentMoves: hasCommentMoves,
                variationType: variationType,
                isNested: true
              });
            }
          });
        }
      }
    } catch (error) {
      console.error('[3Moves] Error getting nested variations:', error);
    }

    if (variationsToShow.length === 0 && nestedVariationsToShow.length === 0) return null;

    // Sort both types of variations by priority: original → authorComment → userComment
    const sortOrder = { 'original': 0, 'authorComment': 1, 'userComment': 2 };
    variationsToShow.sort((a, b) => sortOrder[a.variationType] - sortOrder[b.variationType]);
    nestedVariationsToShow.sort((a, b) => sortOrder[a.variationType] - sortOrder[b.variationType]);

    // Split variations into main and comment variations
    const mainVariations = variationsToShow.filter(varInfo => !varInfo.hasCommentMoves);
    const commentVariations = variationsToShow.filter(varInfo => varInfo.hasCommentMoves);
    
    // Split nested variations into main and comment variations  
    const nestedMainVariations = nestedVariationsToShow.filter(varInfo => !varInfo.hasCommentMoves);
    const nestedCommentVariations = nestedVariationsToShow.filter(varInfo => varInfo.hasCommentMoves);
          
    return (
      <div style={{ marginTop: 16, marginLeft: 8, background: '', borderRadius: 4 }}>
        {/* Two-column layout */}
        <div style={{ display: 'flex', gap: 16 }}>
          
          {/* Left Column: Main Variations */}
          {mainVariations.length > 0 && (
            <div style={{ flex: 1 }}>
              <h4 style={{ 
                color: 'gold', 
                margin: '0 0 12px 0', 
                fontSize: 14, 
                width: '200px',
                paddingBottom: '5px',
                borderBottom: '2px solid gold'
              }}>
                <i className="fa-solid fa-circle-user" style={{ color: 'gold', marginRight: 4 }}></i>
                Main Variations:
              </h4>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {mainVariations.map((varInfo, index) => {
                  // ENHANCED: Handle start position variations
                  const isStartPositionVar = varInfo.isStartPositionVariation;
                  const parentMoveId = isStartPositionVar ? '-1' : String(varInfo.parentIndex);
                  const variationMoveId = `${varInfo.parentIndex}|${varInfo.variationIndex}:0`;
                  
                  return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#ccc', minWidth: mobileStyles.moveNumber.minWidth, fontSize: mobileStyles.moveNumber.fontSize }}>
                        {isStartPositionVar ? '1.' : (() => {
                          const baseNumber = Math.floor(varInfo.parentIndex / 2) + 1;
                          const isWhiteMove = varInfo.parentIndex % 2 === 0;
                          return isWhiteMove ? `${baseNumber}.` : `${baseNumber}...`;
                        })()}
                      </span>
                      
                      {/* Parent move button */}
                      <button
                        onClick={() => goToMove(parentMoveId)}
                        style={{
                          background: '#666',
                          color: 'white',
                          padding: mobileStyles.moveButton.padding,
                          borderRadius: 4,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: mobileStyles.moveButton.fontSize,
                          minWidth: mobileStyles.moveButton.minWidth
                        }}
                        title={isStartPositionVar ? "Go to start position" : `Go to parent move: ${varInfo.parentMove.notation}`}
                      >
                        {isStartPositionVar ? '⧫' : varInfo.parentMove.notation}
                      </button>
                      
                      {/* Arrow separator */}
                      <span style={{ color: 'gold', fontSize: '1.2rem' }}>→</span>
                      
                      {/* First variation move button */}
                      <button
                        onClick={() => goToMove(variationMoveId)}
                        style={{
                          position: 'relative',
                          background: 'gold',
                          color: 'black',
                          padding: mobileStyles.moveButton.padding,
                          borderRadius: 4,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: mobileStyles.moveButton.fontSize,
                          minWidth: mobileStyles.moveButton.minWidth
                        }}
                        title={`Go to variation move: ${varInfo.firstMove.notation}`}
                      >
                        {varInfo.firstMove.notation}
                      </button>
                      
                      {/* Show "..." if there are more moves in the variation */}
                      {varInfo.variation.length > 1 && (
                        <i className="fa-solid fa-ellipsis" style={{ marginLeft: '5px', color: 'gold' }}></i>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Right Column: Comment Variations */}
          {commentVariations.length > 0 && (
            <div style={{ flex: 1 }}>
              <h4 style={{ 
                color: '#ff6600', 
                margin: '0 0 12px 0', 
                fontSize: 14, 
                width: '200px',
                paddingBottom: '5px',
                borderBottom: '2px solid #ff6600'
              }}>
                <i className="fa-solid fa-comments" style={{ color: '#ff6600', marginRight: 4 }}></i>
                Comment Variations:
              </h4>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {commentVariations.map((varInfo, index) => {
                  // ENHANCED: Handle start position comment variations
                  const isStartPositionVar = varInfo.isStartPositionVariation;
                  const parentMoveId = isStartPositionVar ? '-1' : String(varInfo.parentIndex);
                  const variationMoveId = `${varInfo.parentIndex}|${varInfo.variationIndex}:0`;
                  
                  return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* Comment icon prefix */}
                      {/* <span style={{ color: '#ff6600', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        <i className="fa-solid fa-comment"></i>
                      </span> */}
                      
                      <span style={{ color: '#ccc', minWidth: mobileStyles.moveNumber.minWidth, fontSize: mobileStyles.moveNumber.fontSize }}>
                        {isStartPositionVar ? '1.' : (() => {
                          const baseNumber = Math.floor(varInfo.parentIndex / 2) + 1;
                          const isWhiteMove = varInfo.parentIndex % 2 === 0;
                          return isWhiteMove ? `${baseNumber}.` : `${baseNumber}...`;
                        })()}
                      </span>
                      
                      {/* Parent move button */}
                      <button
                        onClick={() => goToMove(parentMoveId)}
                        style={{
                          background: '#666',
                          color: 'white',
                          padding: mobileStyles.moveButton.padding,
                          borderRadius: 4,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: mobileStyles.moveButton.fontSize,
                          minWidth: mobileStyles.moveButton.minWidth
                        }}
                        title={isStartPositionVar ? "Go to start position" : `Go to parent move: ${varInfo.parentMove.notation}`}
                      >
                        {isStartPositionVar ? '⧫' : varInfo.parentMove.notation}
                      </button>
                      
                      {/* Arrow separator */}
                      <span style={{ color: '#ff6600', fontSize: '1.2rem' }}>→</span>
                      
                      {/* First variation move button */}
                      <div style={{ position: 'relative' }}>
                        <button
                          onClick={() => goToMove(variationMoveId)}
                          style={{
                            position: 'relative',
                            background: '#ff6600',
                            color: 'white',
                            padding: mobileStyles.moveButton.padding,
                            borderRadius: 4,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: mobileStyles.moveButton.fontSize,
                            minWidth: mobileStyles.moveButton.minWidth
                          }}
                          title={`Go to comment variation: ${varInfo.firstMove.notation}`}
                        >
                          {varInfo.firstMove.notation}
                          {/* Comment indicator dot
                          <span style={{
                            position: 'absolute',
                            top: -2,
                            right: -2,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: 'red',
                            border: '1px solid white'
                          }}></span> */}
                        </button>
                      </div>
                      
                      {/* Show "..." if there are more moves in the variation */}
                      {varInfo.variation.length > 1 && (
                        <i className="fa-solid fa-ellipsis" style={{ marginLeft: '5px', color: '#ff6600', fontSize: mobileStyles.smallIcon.fontSize }}></i>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Nested Variations Section */}
        {(nestedMainVariations.length > 0 || nestedCommentVariations.length > 0) && (
          <div style={{ marginTop: 0, borderTop: '', paddingTop: 0 }}>
            <div style={{ display: 'flex', gap: 16 }}>
              
              {/* Left Column: Nested Main Variations */}
              {nestedMainVariations.length > 0 && (
                <div style={{ flex: 1 }}>
                  <h4 style={{ 
                    color: 'gold', 
                    margin: '0 0 12px 0', 
                    marginBottom: '10px',
                    fontSize: 14, 
                    width: '200px' 
                  }}>
                    <i className="fa-solid fa-sitemap" style={{ color: 'gold', marginRight: 4 }}></i>
                    Nested Variations:
                  </h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {nestedMainVariations.map((varInfo, index) => {
                      const parentMoveId = varInfo.parentPath;
                      const variationMoveId = `${varInfo.parentPath}|${varInfo.variationIndex}:0`;
                      
                      return (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#ccc', minWidth: mobileStyles.moveNumber.minWidth, fontSize: mobileStyles.moveNumber.fontSize }}>
                            ↳
                          </span>
                          
                          {/* Parent move button */}
                          <button
                            onClick={() => goToMove(parentMoveId)}
                            style={{
                              background: '#666',
                              color: 'white',
                              padding: mobileStyles.moveButton.padding,
                              borderRadius: 4,
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: mobileStyles.moveButton.fontSize,
                              minWidth: mobileStyles.moveButton.minWidth
                            }}
                            title={`Go to parent move: ${varInfo.parentMove.notation}`}
                          >
                            {varInfo.parentMove.notation}
                          </button>
                          
                          {/* Arrow separator */}
                          <span style={{ color: 'gold', fontSize: '1.2rem' }}>→</span>
                          
                          {/* First variation move button */}
                          <button
                            onClick={() => goToMove(variationMoveId)}
                            style={{
                              position: 'relative',
                              background: 'gold',
                              color: 'black',
                              padding: mobileStyles.moveButton.padding,
                              borderRadius: 4,
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: mobileStyles.moveButton.fontSize,
                              minWidth: mobileStyles.moveButton.minWidth
                            }}
                            title={`Go to nested variation move: ${varInfo.firstMove.notation}`}
                          >
                            {varInfo.firstMove.notation}
                          </button>
                          
                          {/* Show "..." if there are more moves in the variation */}
                          {varInfo.variation.length > 1 && (
                            <i className="fa-solid fa-ellipsis" style={{ marginLeft: '5px', color: 'gold' }}></i>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Right Column: Nested Comment Variations */}
              {nestedCommentVariations.length > 0 && (
                <div style={{ flex: 1 }}>
                  <h4 style={{ 
                    color: '#ff6600', 
                    margin: '0 0 10px 0', 
                    fontSize: 14, 
                    width: '200px' 
                  }}>
                    <i className="fa-solid fa-comments" style={{ color: '#ff6600', marginRight: 4 }}></i>
                    Nested Comments:
                  </h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {nestedCommentVariations.map((varInfo, index) => {
                      const parentMoveId = varInfo.parentPath;
                      const variationMoveId = `${varInfo.parentPath}|${varInfo.variationIndex}:0`;
                      
                      return (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#ccc', minWidth: mobileStyles.moveNumber.minWidth, fontSize: mobileStyles.moveNumber.fontSize }}>
                            ↳
                          </span>
                          
                          {/* Parent move button */}
                          <button
                            onClick={() => goToMove(parentMoveId)}
                            style={{
                              background: '#666',
                              color: 'white',
                              padding: mobileStyles.moveButton.padding,
                              borderRadius: 4,
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: mobileStyles.moveButton.fontSize,
                              minWidth: mobileStyles.moveButton.minWidth
                            }}
                            title={`Go to parent move: ${varInfo.parentMove.notation}`}
                          >
                            {varInfo.parentMove.notation}
                          </button>
                          
                          {/* Arrow separator */}
                          <span style={{ color: '#ff6600', fontSize: '1.2rem' }}>→</span>
                          
                          {/* First variation move button */}
                          <div style={{ position: 'relative' }}>
                            <button
                              onClick={() => goToMove(variationMoveId)}
                              style={{
                                position: 'relative',
                                background: '#ff6600',
                                color: 'white',
                                padding: mobileStyles.moveButton.padding,
                                borderRadius: 4,
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: mobileStyles.moveButton.fontSize,
                                minWidth: mobileStyles.moveButton.minWidth
                              }}
                              title={`Go to nested comment variation: ${varInfo.firstMove.notation}`}
                            >
                              {varInfo.firstMove.notation}
                            </button>
                          </div>
                          
                          {/* Show "..." if there are more moves in the variation */}
                          {varInfo.variation.length > 1 && (
                            <i className="fa-solid fa-ellipsis" style={{ marginLeft: '5px', color: '#ff6600', fontSize: mobileStyles.smallIcon.fontSize }}></i>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper functions for variation navigation
  const getCurrentVariationIndex = (parentIndex) => {
    if (!currentMove.includes('|')) return -1;
    
    const parts = currentMove.split('|');
    const currentParentIndex = parseInt(parts[0], 10);
    
    if (currentParentIndex !== parentIndex) return -1;
    
    const [varIdx] = parts[1].split(':').map(Number);
    return varIdx;
  };

  // Helper function to determine variation colors and types
  const getVariationInfo = () => {
    if (!currentMove.includes('|')) return { isInVariation: false };
    
    const parts = currentMove.split('|');
    const mainMoveIdx = parseInt(parts[0], 10);
    const [variationIdx] = parts[1].split(':').map(Number);
    
    const mainMove = moves[mainMoveIdx];
    if (!mainMove?.variations?.[variationIdx]) return { isInVariation: false };
    
    const variation = mainMove.variations[variationIdx];
    
    
    
    // Check if this variation contains comment moves
    const hasCommentMoves = variation.some(varMove => {
      const isComment = varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT;
      
      return isComment;
    });
    
    const result = {
      isInVariation: true,
      isCommentVariation: hasCommentMoves,
      color: hasCommentMoves ? '#ff6600' : 'gold'
    };
    
    
    
    return result;
  };

  // Helper function to determine variation arrow color for a move with variations
  const getVariationArrowColor = (move, moveId) => {
    if (!hasVariations(move)) return 'gold'; // Default fallback
    
    // Check if we're in a variation view - if so, determine color based on current variation type
    if (currentMove.includes('|')) {
      const parts = currentMove.split('|');
      const currentMainMoveIdx = parseInt(parts[0], 10);
      const [currentVariationIdx] = parts[1].split(':').map(Number);
      
      // If this is the parent move we came from, check the current variation type
      if (moveId === String(currentMainMoveIdx)) {
        const parentMove = moves[currentMainMoveIdx];
        if (parentMove?.variations?.[currentVariationIdx]) {
          const currentVariation = parentMove.variations[currentVariationIdx];
          const isCommentVariation = currentVariation.some(varMove => 
            varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
          );
          return isCommentVariation ? '#ff6600' : 'gold';
        }
      }
    }
    
    // For main line view: check all variations of this move
    let hasOriginalVariations = false;
    let hasCommentVariations = false;
    
    if (move.variations) {
      move.variations.forEach(variation => {
        const hasCommentMoves = variation.some(varMove => 
          varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
        );
        
        if (hasCommentMoves) {
          hasCommentVariations = true;
        } else {
          hasOriginalVariations = true;
        }
      });
    }
    
    // Gold supersedes orange - if there are any original variations, use gold
    return hasOriginalVariations ? 'gold' : '#ff6600';
  };

  // ENHANCED: Helper function to determine variation arrow color for start position variations
  const getStartPositionVariationColor = () => {
    if (!state?.startPosition?.variations || state.startPosition.variations.length === 0) {
      return 'gold'; // Default fallback
    }
    
    // Check if we're in a start position variation view - if so, determine color based on current variation type
    if (currentMove.includes('|') && currentMove.startsWith('-1|')) {
      const parts = currentMove.split('|');
      const [currentVariationIdx] = parts[1].split(':').map(Number);
      
      if (state.startPosition.variations[currentVariationIdx]) {
        const currentVariation = state.startPosition.variations[currentVariationIdx];
        const isCommentVariation = currentVariation.some(varMove => 
          varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
        );
        return isCommentVariation ? '#ff6600' : 'gold';
      }
    }
    
    // For main line view: check all start position variations
    let hasOriginalVariations = false;
    let hasCommentVariations = false;
    
    state.startPosition.variations.forEach(variation => {
      const hasCommentMoves = variation.some(varMove => 
        varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
      );
      
      if (hasCommentMoves) {
        hasCommentVariations = true;
      } else {
        hasOriginalVariations = true;
      }
    });
    
    // Gold supersedes orange - if there are any original variations, use gold
    return hasOriginalVariations ? 'gold' : '#ff6600';
  };

  // Helper function to determine border color for parent moves
  const getParentBorderColor = () => {
    if (!currentMove.includes('|')) return 'none';
    
    const parts = currentMove.split('|');
    const mainMoveIdx = parseInt(parts[0], 10);
    
    const mainMove = moves[mainMoveIdx];
    if (!mainMove?.variations) return 'none';
    
    let hasOriginalVariations = false;
    let hasCommentVariations = false;
    
    
    
    mainMove.variations.forEach((variation, idx) => {
      const hasCommentMoves = variation.some(varMove => {
        const isComment = varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT;
        
        return isComment;
      });
      
      
      
      if (hasCommentMoves) {
        hasCommentVariations = true;
      } else {
        hasOriginalVariations = true;
      }
    });
    
    
    
    // Author variations supersede comment variations
    if (hasOriginalVariations) {
      return '3px solid gold';
    } else if (hasCommentVariations) {
      return '3px solid #ff6600';
    }
    
    return '3px solid gold'; // Default fallback
  };

  const canNavigateVariation = (direction, parentIndex) => {
    if (!currentMove.includes('|')) return false;
    
    const pathParts = currentMove.split('|');
    const isNestedVariation = pathParts.length > 2;
    
    if (isNestedVariation) {
      // For nested variations, we need to check if we can navigate between nested variations
      return canNavigateNestedVariation(direction);
    } else {
      // For first-level variations, use the existing logic
      const currentVarIndex = getCurrentVariationIndex(parentIndex);
      if (currentVarIndex === -1) return false;
      
      const parentMove = moves[parentIndex];
      if (!parentMove?.variations) return false;
      
      const totalVariations = parentMove.variations.length;
      
      if (direction === 'up') {
        return currentVarIndex > 0;
      } else {
        return currentVarIndex < totalVariations - 1;
      }
    }
  };

  const canNavigateNestedVariation = (direction) => {
    if (!currentMove.includes('|')) return false;
    
    const pathParts = currentMove.split('|');
    if (pathParts.length <= 2) return false; // Not a nested variation
    
    // Get the parent variation path (everything except the last segment)
    const parentVariationPath = pathParts.slice(0, -1).join('|');
    
    // Get the current nested variation index from the last segment
    const lastSegment = pathParts[pathParts.length - 1];
    const [currentNestedVarIndex] = lastSegment.split(':').map(Number);
    
    // Get the parent variation move to check its variations
    const parentResult = getNodeAtPathInfinite(moves, parentVariationPath);
    if (!parentResult.success || !parentResult.node) return false;
    
    const parentVariationMove = parentResult.node;
    if (!parentVariationMove.variations) return false;
    
    const totalNestedVariations = parentVariationMove.variations.length;
    
    if (direction === 'up') {
      return currentNestedVarIndex > 0;
    } else {
      return currentNestedVarIndex < totalNestedVariations - 1;
    }
  };

  const navigateVariation = (direction, parentIndex, variationsToShow) => {
    

    const pathParts = currentMove.split('|');
    const isNestedVariation = pathParts.length > 2;
    
    if (isNestedVariation) {
      // Handle nested variation navigation
      navigateNestedVariation(direction);
    } else {
      // Handle first-level variation navigation (existing logic)
      const currentVarIndex = getCurrentVariationIndex(parentIndex);
      

      if (currentVarIndex === -1) {
        
        return;
      }
      
      let newVarIndex;
      if (direction === 'up') {
        newVarIndex = Math.max(0, currentVarIndex - 1);
      } else {
        newVarIndex = Math.min(variationsToShow.length - 1, currentVarIndex + 1);
      }
      
      
      
      if (newVarIndex !== currentVarIndex) {
        const newMoveId = `${parentIndex}|${newVarIndex}:0`;
        
        
        goToMove(newMoveId);
      } else {
        
      }
    }
  };

  const navigateNestedVariation = (direction) => {
    

    const pathParts = currentMove.split('|');
    if (pathParts.length <= 2) return; // Not a nested variation
    
    // Get the parent variation path and current nested variation index
    const parentVariationPath = pathParts.slice(0, -1).join('|');
    const lastSegment = pathParts[pathParts.length - 1];
    const [currentNestedVarIndex, currentMoveIndex] = lastSegment.split(':').map(Number);
    
    
    
    // Get the parent variation move to find available nested variations
    const parentResult = getNodeAtPathInfinite(moves, parentVariationPath);
    if (!parentResult.success || !parentResult.node) {
      
      return;
    }
    
    const parentVariationMove = parentResult.node;
    if (!parentVariationMove.variations) {
      
      return;
    }
    
    const totalNestedVariations = parentVariationMove.variations.length;
    
    let newNestedVarIndex;
    if (direction === 'up') {
      newNestedVarIndex = Math.max(0, currentNestedVarIndex - 1);
    } else {
      newNestedVarIndex = Math.min(totalNestedVariations - 1, currentNestedVarIndex + 1);
    }
    
    
    
    if (newNestedVarIndex !== currentNestedVarIndex) {
      // Build the new path: parent variation path + new nested variation + move 0
      const newMoveId = `${parentVariationPath}|${newNestedVarIndex}:0`;
      
      
      goToMove(newMoveId);
    } else {
      
    }
  };

  // Check if the starting position has black to move
  // Use startingTurn prop if provided, otherwise calculate from initialFEN
  const rootStartColor = startingTurn || (initialFEN ? (() => {
    try {
      const chess = new Chess(initialFEN);
      return chess.turn();
    } catch (e) {
      return 'w';
    }
  })() : 'w');

  // Split the current move ID into path parts
  const pathParts = currentMove.split('|');
  
  // Resolve the current position in the move tree
  const currentLayer = resolvePath(moves, pathParts);
  
  // Get the starting color for the current variation
  const currentStartColor = currentLayer.from 
    ? getStartColor(currentLayer.from) 
    : rootStartColor;
  
  // Find the current trio
  const currentFocus = focusTrio(
    currentLayer.moves, 
    currentLayer.focus, 
    currentStartColor,
    currentMove
  );
  
  // If we're in a variation, find the parent position
  const parentLayer = currentLayer.from 
    ? resolvePath(moves, currentLayer.from.split('|')) 
    : null;
  
  // Custom parent context renderer - shows immediate parent level (not main line when nested)
  const renderParentContext = () => {
    if (!currentMove.includes('|')) return null;
    
    const parts = currentMove.split('|');
    const isNestedVariation = parts.length > 2; // More than main|variation means nested
    const isStartPositionVariation = parts[0] === '-1'; // Start position variation
    
    if (isStartPositionVariation) {
      // ENHANCED: Handle start position variations - show "Start Position" as main line
      return (
        <div style={{ 
          padding: '8px 12px',
          borderRadius: 4,
          borderLeft: '3px solid #4a90e2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '30px' }}>
            {/* ENHANCED: Home button to return to main line parent */}
            <button
              onClick={() => goToMove('-1')}
              style={{
                background: 'transparent',
                color: '#4a90e2',
                border: '1px solid #4a90e2',
                borderRadius: 4,
                padding: '2px 6px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                marginRight: '8px'
              }}
              title="Return to start position (main line parent)"
            >
              <i className="fa-solid fa-home"></i>
            </button>
            
            <span style={{ color: '#4a90e2', fontSize: '1.5rem', fontWeight: 'bold', paddingLeft: '', marginRight: '12px' }}>
              <i className="fa-solid fa-circle-user" style={{}}></i>&nbsp;
              Main Line:
            </span>
            <span style={{ color: '#999', minWidth: 24 }}>1.</span>
            
            {/* Start position button */}
            <button
              onClick={() => goToMove('-1')}
              style={{
                background: '#666',
                color: 'white',
                padding: mobileStyles.moveButton.padding,
                borderRadius: 4,
                border: 'none',
                cursor: 'pointer',
                fontSize: mobileStyles.moveButton.fontSize,
                minWidth: mobileStyles.moveButton.minWidth
              }}
              title="Go to start position"
            >
              ⧫
            </button>
            
            <span style={{ color: '#4a90e2', fontSize: '1.5rem', fontWeight: 'bold' }}>
              →
            </span>
            
            {/* First main line move if it exists */}
            {moves.length > 0 && renderMoveButton(moves[0], '0', '#666', true)}
          </div>
        </div>
      );
    } else if (isNestedVariation) {
      // For nested variations, show the parent variation as the "main" line
      const parentVariationPath = parts.slice(0, -1).join('|');
      const parentVariationParts = parentVariationPath.split('|');
      
      // Get the parent variation moves
      const parentResult = getNodeAtPathInfinite(moves, parentVariationPath);
      if (!parentResult.success) return null;
      
      const parentVariationArray = parentResult.nodeArray;
      const parentMoveIndex = parentResult.nodeIndex;
      
      if (!parentVariationArray || parentMoveIndex < 0) return null;
      
      const parentMove = parentVariationArray[parentMoveIndex];
      const nextParentMove = parentVariationArray[parentMoveIndex + 1] || null;
      
      if (!parentMove) return null;
      
      // Calculate display info for parent variation
      const mainLineIndex = parseInt(parentVariationParts[0], 10);
      const baseNumber = Math.floor(mainLineIndex / 2) + 1;
      const variationNumber = Math.floor(parentMoveIndex / 2) + 1;
      const isParentWhite = parentMoveIndex % 2 === 0;
      
      return (
        <div style={{ 
          padding: '8px 12px',
          borderRadius: 4,
          borderLeft: '3px solid #4a90e2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* ENHANCED: Home button to return to main line parent */}
            <button
              onClick={() => {
                // For nested variations, go to the main line move that this variation branches from
                const mainLineIndex = parseInt(parentVariationParts[0], 10);
                goToMove(String(mainLineIndex));
              }}
              style={{
                background: 'transparent',
                color: '#4a90e2',
                border: '1px solid #4a90e2',
                borderRadius: 4,
                padding: '2px 6px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                marginRight: '8px'
              }}
              title="Return to main line parent"
            >
              <i className="fa-solid fa-home"></i>
            </button>
            
            {isParentWhite ? (
              <>
                <span style={{ color: '#999', minWidth: 24 }}>{variationNumber}.</span>
                {renderMoveButton(parentMove, parentVariationPath, '#666')}
                
                <span style={{ color: '#4a90e2', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  →
                </span>
                
                {nextParentMove && renderMoveButton(nextParentMove, `${parentVariationPath.split('|')[0]}|${parentVariationParts[1].split(':')[0]}:${parentMoveIndex + 1}`, '#666', true)}
                
                <span style={{ color: '#999', fontSize: '0.9rem', fontWeight: 'bold', paddingLeft: '12px' }}>
                  (Parent Variation)
                </span>
              </>
            ) : (
              <>
                <span style={{ color: '#999', minWidth: 24 }}>{variationNumber}...</span>
                {renderMoveButton(parentMove, parentVariationPath, '#666')}
                
                {nextParentMove && (
                  <>
                    <span style={{ color: '#4a90e2', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      →
                    </span>
                    
                    <span style={{ color: '#999', minWidth: 24 }}>{variationNumber + 1}.</span>
                    {renderMoveButton(nextParentMove, `${parentVariationPath.split('|')[0]}|${parentVariationParts[1].split(':')[0]}:${parentMoveIndex + 1}`, '#666', true)}
                  </>
                )}
                
                <span style={{ color: '#999', fontSize: '0.9rem', fontWeight: 'bold', paddingLeft: '12px' }}>
                  (Parent Variation)
                </span>
              </>
            )}
          </div>
        </div>
      );
    } else {
      // For first-level variations, show main line as before
      const parentMoveIdx = parseInt(parts[0], 10);
      const parentMove = moves[parentMoveIdx];
      const nextMainLineMove = moves[parentMoveIdx + 1] || null;
      
      if (!parentMove) return null;
      
      // Calculate move numbers for proper display
      const parentMoveNumber = Math.floor(parentMoveIdx / 2) + 1;
      const isParentWhite = parentMoveIdx % 2 === 0;
      
      // Determine arrow color
      const parentArrowColor = (() => {
        const mainMove = moves[parentMoveIdx];
        if (!mainMove?.variations) return 'gold';
        
        let hasOriginalVariations = false;
        let hasCommentVariations = false;
        
        mainMove.variations.forEach(variation => {
          const hasCommentMoves = variation.some(varMove => 
            varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
          );
          
          if (hasCommentMoves) {
            hasCommentVariations = true;
          } else {
            hasOriginalVariations = true;
          }
        });
        
        return hasOriginalVariations ? 'gold' : '#ff6600';
      })();
      
      return (
        <div style={{ 
          padding: '8px 12px',
          borderRadius: 4,
          borderLeft: '3px solid #4a90e2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '30px' }}>
            {/* ENHANCED: Home button to return to main line parent */}
            <button
              onClick={() => {
                // For first-level variations, go to the main line move that this variation branches from
                goToMove(String(parentMoveIdx));
              }}
              style={{
                background: 'transparent',
                color: '#4a90e2',
                border: '1px solid #4a90e2',
                borderRadius: 4,
                padding: '2px 6px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                marginRight: '8px'
              }}
              title="Return to main line parent"
            >
              <i className="fa-solid fa-home"></i>
            </button>
            
            {isParentWhite ? (
              // Parent is white: Show "2. c4 → Nf6"
              <>
              <span style={{ color: '#4a90e2', fontSize: '1.5rem', fontWeight: 'bold', paddingLeft: '', marginRight: '12px' }}>
              <i className="fa-solid fa-circle-user" style={{}}></i>&nbsp;
              Main Line:
              </span>
                <span style={{ color: '#999', minWidth: 24 }}>{parentMoveNumber}.</span>
                {renderMoveButton(parentMove, String(parentMoveIdx), '#666')}
                
                <span style={{ color: '#4a90e2', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  →
                </span>
                
                {nextMainLineMove && renderMoveButton(nextMainLineMove, String(parentMoveIdx + 1), '#666', true)}
                {/* Main Line label */}
            
              </>
            ) : (
              // Parent is black: Show "2... Nf6 → 3. e3"
              <>
              <span style={{ color: '#4a90e2', fontSize: '1.5rem', fontWeight: 'bold', paddingLeft: '', marginRight: '12px' }}>
                Main Line:
                </span>
                <span style={{ color: '#999', minWidth: 24 }}>{parentMoveNumber}...</span>
                {renderMoveButton(parentMove, String(parentMoveIdx), '#666')}
                
                {nextMainLineMove && (
                  <>
                    <span style={{ color: '#4a90e2', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      →
                    </span>
                    
                    <span style={{ color: '#999', minWidth: 24 }}>{parentMoveNumber + 1}.</span>
                    {renderMoveButton(nextMainLineMove, String(parentMoveIdx + 1), '#666', true)}
                  </>
                )}
                
                
              </>
            )}
          </div>
        </div>
      );
    }
  };

      // Debugging - check what we're working with
    // console.log('FINAL RENDER DATA:', {
    //   currentLayer,
    //   currentFocus,
    //   parentLayer,
    //   parentContext
    // });

  // FINAL CONSOLE LOG SUMMARY
  const commentMovesInCurrentLayer = currentLayer.moves ? currentLayer.moves.filter(isCommentMove) : [];
  if (commentMovesInCurrentLayer.length > 0 || type !== 'DEFAULT') {
    
  }

  // Helper functions for variation captions and move content (only for ViewGame)
  const isInVariation = (moveIdx) => {
    return moveIdx && moveIdx.toString().includes('|');
  };

  const getVariationCaption = (moveIdx) => {
    if (!isViewGame || !moves) return null;
    
    try {
      if (isInVariation(moveIdx)) {
        const parts = moveIdx.split('|');
        
        // For nested variations, we need to check the most deeply nested level first
        if (parts.length > 2) {
          // This is a nested variation - get the deepest level caption first
          const result = getNodeAtPathInfinite(moves, moveIdx);
          if (result.success) {
            // Try to get the parent path to find the variation that contains this move
            const parentPath = getParentPath(moveIdx);
            if (parentPath) {
              const parentResult = getNodeAtPathInfinite(moves, parentPath);
              if (parentResult.success && parentResult.node?.variationCaptions) {
                // Get the variation index from the current move path
                const lastSegment = parts[parts.length - 1];
                const [varIdx] = lastSegment.split(':').map(Number);
                const caption = parentResult.node.variationCaptions[varIdx];
                if (caption) return caption;
              }
            }
          }
        } else {
          // Regular first-level variation or start position variation
          const mainMoveIdx = parseInt(parts[0]);
          const [variationIdx] = parts[1].split(':').map(Number);
          
          // ENHANCED: Handle start position variations
          if (mainMoveIdx === -1) {
            // Start position variation
            if (state?.startPosition?.variationCaptions && state.startPosition.variationCaptions[variationIdx]) {
              const caption = state.startPosition.variationCaptions[variationIdx];
              return caption;
            }
          } else {
            // Regular main line variation
            const mainMove = moves[mainMoveIdx];
            if (mainMove && mainMove.variationCaptions && mainMove.variationCaptions[variationIdx]) {
              const caption = mainMove.variationCaptions[variationIdx];
              return caption;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error getting variation caption:', error);
    }
    
    return null;
  };

  const getCommentCaption = (moveIdx) => {
    
    
    if (!isViewGame || !moves || !comments) {
      
      return null;
    }
    
    try {
      if (isInVariation(moveIdx)) {
        const parts = moveIdx.split('|');
        
        // Enhanced logic for nested variations - always check the most deeply nested variation
        if (parts.length > 2) {
          // This is a nested variation - find the deepest level variation that contains comment moves
          
          
          // Get the parent path to find the variation that contains the current move
          const parentPath = getParentPath(moveIdx);
          if (parentPath) {
            const parentResult = getNodeAtPathInfinite(moves, parentPath);
            if (parentResult.success && parentResult.node?.variations) {
              // Get the variation index from the current move path
              const lastSegment = parts[parts.length - 1];
              const [varIdx] = lastSegment.split(':').map(Number);
              const variation = parentResult.node.variations[varIdx];
              
              if (variation) {
                
                
                // Check if this nested variation contains comment moves
                const commentMoves = variation.filter(varMove => 
                  varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
                );
                
                if (commentMoves.length > 0) {
                  // Get the creator ID from the first comment move
                  const firstCommentMove = commentMoves[0];
                  const creatorId = firstCommentMove.creator.id;
                  
                  
                  
                  // Find the matching comment in the comments array
                  const matchingComment = comments.find(comment => comment.id === creatorId);
                  
                  if (matchingComment && matchingComment.content) {
                    
                    return matchingComment.content;
                  }
                }
              }
            }
          }
        } else {
          // Regular first-level variation
          const mainMoveIdx = parseInt(parts[0]);
          const [variationIdx] = parts[1].split(':').map(Number);
          
          const mainMove = moves[mainMoveIdx];
          if (mainMove && mainMove.variations && mainMove.variations[variationIdx]) {
            const variation = mainMove.variations[variationIdx];
            
            
            
            // Check if this variation contains comment moves
            const commentMoves = variation.filter(varMove => 
              varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
            );
            
            
            
            if (commentMoves.length > 0) {
              // Get the creator ID from the first comment move
              const firstCommentMove = commentMoves[0];
              const creatorId = firstCommentMove.creator.id;
              
              
              
              
              // Find the matching comment in the comments array
              const matchingComment = comments.find(comment => comment.id === creatorId);
              
              if (matchingComment && matchingComment.content) {
                
                return matchingComment.content;
              } else {
                
              }
            } else {
              
            }
          } else {
            
          }
        }
      } else {
        
      }
    } catch (error) {
      console.error('[3Moves] Error getting comment caption:', error);
    }
    
    return null;
  };

  const getAnyCaption = (moveIdx) => {
    
    
    // For nested variations, we want to prioritize the most deeply nested caption
    // Check if this is a nested variation (more than 2 parts in path)
    if (isInVariation(moveIdx)) {
      const parts = moveIdx.split('|');
      
      if (parts.length > 2) {
        // This is a nested variation - prioritize comment captions for nested levels
        const commentCaption = getCommentCaption(moveIdx);
        if (commentCaption) {
          
          return { type: 'comment', caption: commentCaption };
        }
        
        // If no comment caption, try variation caption
        const variationCaption = getVariationCaption(moveIdx);
        if (variationCaption) {
          
          return { type: 'variation', caption: variationCaption };
        }
      } else {
        // For first-level variations, try variation caption first (original behavior)
        const variationCaption = getVariationCaption(moveIdx);
        if (variationCaption) {
          
          return { type: 'variation', caption: variationCaption };
        }
        
        // If no variation caption, try comment caption
        const commentCaption = getCommentCaption(moveIdx);
        if (commentCaption) {
          
          return { type: 'comment', caption: commentCaption };
        }
      }
    }
    
    
    return null;
  };

  const getCurrentMoveContent = (moveIdx) => {
    
    
    if (!isViewGame || !moves) {
      
      return null;
    }
    
    try {
      // ENHANCED: Handle start position
      if (moveIdx === '-1') {
        const content = state?.startPosition?.content || null;
        return content;
      }
      
      if (isInVariation(moveIdx)) {
        // For variation moves (including nested), use infinite path resolution
        // This should already handle start position variations through getNodeAtPathInfinite
        const result = getNodeAtPathInfinite(moves, moveIdx, state);
        if (result.success && result.node) {
          const content = result.node.content || null;
          
          return content;
        } else {
          
        }
      } else {
        // For main line moves
        const moveIndex = parseInt(moveIdx);
        if (moveIndex >= 0 && moveIndex < moves.length) {
          const content = moves[moveIndex]?.content || null;
          
          return content;
        }
      }
    } catch (error) {
      console.error('Error getting move content:', error);
    }
    
    
    return null;
  };

  /**
   * Check if a move is starred
   * @param {Object} move - Move object
   * @param {string} moveId - Move ID/path
   * @returns {boolean} - True if the move is starred
   */
  const isStarred = (move, moveId) => {
    if (!starredMoves || !Array.isArray(starredMoves)) return false;
    
    // Check if this specific move ID is in the starred moves array
    return starredMoves.includes(moveId);
  };

  // Check if we're dealing with a starting position variation
  const parts = currentMove.split('|');
  const isStartPositionVariation = parts[0] === '-1';

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      // height: '95%', // CSS can handle both numeric and string values
      height: isViewGame ? '95%' : 'calc(100% - 300px)',
      borderRadius: 4,
      minWidth: '540px',
      maxWidth: '540px'
    }}>
      {/* Sticky Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        padding: '0px 12px',
        paddingBottom: '10px',
      }}>

        {/* Two-Column Layout: Variation Controls + Moves */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          
          {/* Left Column: Variation Navigation Controls */}
          {(() => {
            const getCurrentMoveInfo = () => {
              if (currentMove === '-1') {
                return { isStart: true, parentIndex: -1, inVariation: false };
              }

              if (currentMove.includes('|')) {
                // We're in a variation - extract parent move index
                const parts = currentMove.split('|');
                const parentIndex = parseInt(parts[0], 10);
                return { isStart: false, parentIndex, inVariation: true };
              } else {
                // We're on main line
                const parentIndex = parseInt(currentMove, 10);
                return { isStart: false, parentIndex, inVariation: false };
              }
            };

            // Enhanced helper functions for nested variation detection
            const isInNestedVariation = () => {
              if (!currentMove.includes('|')) return false;
              const parts = currentMove.split('|');
              return parts.length > 2; // More than 2 parts means nested
            };

            const currentMoveHasNestedVariations = () => {
              try {
                // Get the actual move object at current position
                const nodeResult = getNodeAtPathInfinite(moves, currentMove);
                if (!nodeResult.success) return false;
                
                return nodeResult.node?.variations?.length > 0;
              } catch (error) {
                console.error('[3Moves] Error checking nested variations:', error);
                return false;
              }
            };

            const currentMoveInfo = getCurrentMoveInfo();
            
            // Enhanced condition to show controls for 3-level hierarchy
            const shouldShowControls = currentMoveInfo.inVariation || 
              isInNestedVariation() || 
              currentMoveHasNestedVariations() ||
              (currentMoveInfo.parentIndex >= 0 && 
               currentMoveInfo.parentIndex < moves.length && 
               moves[currentMoveInfo.parentIndex]?.variations?.length > 0);

            if (!shouldShowControls) return null;

            const targetParentIndex = currentMoveInfo.parentIndex;
            const variationsToShow = [];

            if (targetParentIndex >= 0 && targetParentIndex < moves.length) {
              const parentMove = moves[targetParentIndex];
              
              if (parentMove.variations && parentMove.variations.length > 0) {
                parentMove.variations.forEach((variation, varIndex) => {
                  if (variation.length > 0) {
                    const hasCommentMoves = variation.some(varMove => 
                      varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
                    );
                    
                    let variationType = 'original';
                    if (hasCommentMoves) {
                      const isAuthorComment = variation.some(varMove => 
                        varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
                        varMove?.creator?.id === userId
                      );
                      variationType = isAuthorComment ? 'authorComment' : 'userComment';
                    }
                    
                    variationsToShow.push({
                      parentMove: parentMove,
                      parentIndex: targetParentIndex,
                      variationIndex: varIndex,
                      variation: variation,
                      firstMove: variation[0],
                      hasCommentMoves: hasCommentMoves,
                      variationType: variationType
                    });
                  }
                });
              }
            }

            // Enhanced multi-level navigation check
            const hasStartPositionVariations = currentMove === '-1' && 
              state?.startPosition?.variations && 
              state.startPosition.variations.length > 0;
              
            const hasMultiLevelNavigation = 
              variationsToShow.length > 1 || 
              isInNestedVariation() || 
              currentMoveHasNestedVariations() ||
              hasStartPositionVariations;

            // Only render the controls if we have multi-level navigation
            if (!hasMultiLevelNavigation) return null;

            // return (
            //   <div style={{ 
            //     display: 'flex', 
            //     flexDirection: 'column',
            //     gap: 8,
            //     alignItems: 'center',
            //     minWidth: 'fit-content'
            //   }}>
            //     {/* Home button - always go to main line when in any variation */}
            //     {(currentMoveInfo.inVariation || isInNestedVariation()) && (
            //       <button
            //         onClick={() => {
            //           // Always go to main line parent - extract the main line index
            //           const parts = currentMove.split('|');
            //           const mainLineIndex = parts[0];
            //           goToMove(mainLineIndex);
            //         }}
            //         style={{
            //           // background: '#666',
            //           background: 'transparent',
            //           color: 'gray',
            //           border: 'none',
            //           borderRadius: 4,
            //           padding: '8px',
            //           cursor: 'pointer',
            //           fontSize: '14px',
            //           display: 'flex',
            //           alignItems: 'center',
            //           gap: 4,
            //           minWidth: 'fit-content'
            //         }}
            //         title="Return to main line"
            //       >
            //         <i className="fa-solid fa-flip-horizontal fa-home"></i>
                    
            //       </button>
            //     )}

            //     {/* Enhanced Up/Down arrows for nested variation navigation */}
            //     {hasMultiLevelNavigation && (
            //       <>
            //         {/* Up Arrow - Only show when IN a variation (not on main line) */}
            //         {(currentMoveInfo.inVariation || isInNestedVariation()) && (
            //           <button
            //             onClick={() => {
            //               if (isInNestedVariation()) {
            //                 // We're in nested variation - go up to parent variation level
            //                 const parentPath = getParentPath(currentMove);
                            
            //                 goToMove(parentPath);
            //               } else if (currentMoveInfo.inVariation) {
            //                 // We're in first-level variation - navigate to sibling or exit to main
            //                 const currentVarIndex = getCurrentVariationIndex(targetParentIndex);
            //                 if (currentVarIndex > 0) {
            //                   navigateVariation('up', targetParentIndex, variationsToShow);
            //                 } else {
            //                   // Go to main line parent
            //                   goToMove(String(targetParentIndex));
            //                 }
            //               }
            //             }}
            //             style={{
            //               // background: '#4a90e2',
            //               background: 'transparent',
            //               color: 'gray',
            //               border: 'none',
            //               borderRadius: 4,
            //               padding: '6px 10px',
            //               cursor: 'pointer',
            //               fontSize: '14px',
            //               minWidth: 'fit-content'
            //             }}
            //             title={isInNestedVariation() ? "Go to parent variation" : "Previous variation or main line"}
            //           >
            //             <i className="fa-solid fa-arrow-up"></i>
            //           </button>
            //         )}

            //         {(() => {
            //           // Determine the appropriate color based on variation type
            //           let arrowColor = '#4a90e2'; // Default blue
                      
            //           if (currentMoveHasNestedVariations()) {
            //             // Check the type of nested variations on current move
            //             try {
            //               const nodeResult = getNodeAtPathInfinite(moves, currentMove);
            //               if (nodeResult.success && nodeResult.node?.variations?.length > 0) {
            //                 const hasCommentVariations = nodeResult.node.variations.some(variation => 
            //                   variation.some(varMove => varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT)
            //                 );
            //                 const hasOriginalVariations = nodeResult.node.variations.some(variation => 
            //                   !variation.some(varMove => varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT)
            //                 );
                            
            //                 // Gold supersedes orange - if there are any original variations, use gold
            //                 arrowColor = hasOriginalVariations ? '#ffd700' : '#ff6600';
            //               }
            //             } catch (error) {
            //               console.error('[3Moves] Error determining nested variation color:', error);
            //             }
            //           } else if (!currentMoveInfo.inVariation && variationsToShow.length > 0) {
            //             // Check the type of variations available from main line
            //             const hasCommentVariations = variationsToShow.some(varInfo => varInfo.hasCommentMoves);
            //             const hasOriginalVariations = variationsToShow.some(varInfo => !varInfo.hasCommentMoves);
                        
            //             // Gold supersedes orange - if there are any original variations, use gold
            //             arrowColor = hasOriginalVariations ? '#ffd700' : '#ff6600';
            //           }
                      
            //                                  const canNavigateDown = currentMoveHasNestedVariations() || variationsToShow.length > 1 || (!currentMoveInfo.inVariation && variationsToShow.length > 0) || hasStartPositionVariations;
                       
            //            // Check if this is the only button (main line case) to add margin
            //            const isOnlyButton = !currentMoveInfo.inVariation && !isInNestedVariation();
                       
            //            return canNavigateDown ? (
            //             <button
            //               onClick={() => {
            //                 if (hasStartPositionVariations) {
            //                   // Navigate from start position to first start position variation
            //                   goToMove('-1|0:0');
            //                 } else if (currentMoveHasNestedVariations()) {
            //                   // Current move has nested variations - enter the first one
            //                   try {
            //                     const nodeResult = getNodeAtPathInfinite(moves, currentMove);
            //                     if (nodeResult.success && nodeResult.node?.variations?.length > 0) {
            //                       const nestedVariationPath = `${currentMove}|0:0`;
                                  
            //                       goToMove(nestedVariationPath);
            //                     }
            //                   } catch (error) {
            //                     console.error('[3Moves] Error entering nested variation:', error);
            //                   }
            //                 } else if (currentMoveInfo.inVariation && variationsToShow.length > 1) {
            //                   // Navigate to next sibling variation
            //                   navigateVariation('down', targetParentIndex, variationsToShow);
            //                 } else if (!currentMoveInfo.inVariation && variationsToShow.length > 0) {
            //                   // Enter first variation from main line
            //                   const firstVariation = variationsToShow[0];
            //                   const firstVariationPath = `${firstVariation.parentIndex}|${firstVariation.variationIndex}:0`;
                              
            //                   goToMove(firstVariationPath);
            //                 }
            //               }}
            //               style={{
            //                 // background: arrowColor,
            //                 background: 'transparent',
            //                 color: 'gray',
            //                 border: 'none',
            //                 borderRadius: 4,
            //                 padding: '6px 10px',
            //                 cursor: 'pointer',
            //                 fontSize: '14px',
            //                 minWidth: 'fit-content',
            //                 marginTop: isOnlyButton ? '7px' : '0'
            //               }}
            //               title={currentMoveHasNestedVariations() ? "Enter nested variation" : "Next variation or enter variation"}
            //             >
            //               <i className="fa-solid fa-arrow-down"></i>
            //             </button>
            //            ) : null;
            //         })()}
            //       </>
            //     )}
            //   </div>
            // );
          })()}

          {/* Right Column: Main Content Area */}
          <div style={{ flex: 1 }}>
            {/* Main Line Display - Always Visible */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Top Row - Parent Context */}
              {renderParentContext()}

              {/* Variation Caption Area - Only for AddGame mode and only for variation captions */}
              {!isViewGame && isMoveInVariation && isMoveInVariation(currentMove, moves) && (
                <div style={{ position: 'relative', width: '100%' }}>
                  <textarea 
                    placeholder="Caption this variation..."
                    maxLength="1000"
                    style={{
                      width: '95%',
                      height: '20px',
                      padding: '10px',
                      backgroundColor: '#222',
                      color: 'white',
                      border: '2px solid gold',
                      borderRadius: '5px',
                      fontSize: '18px',
                      resize: 'none',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'gold #222',
                    }}
                    value={variationContent}
                    onChange={(e) => {
                      if (setSkipMoveIndexUpdate) setSkipMoveIndexUpdate(true);
                      if (setIsAnnotationFocused) setIsAnnotationFocused(true);
                      
                      const newContent = e.target.value;
                      const cleanedContent = cleanProfanity(newContent);
                      if (setVariationContent) setVariationContent(cleanedContent);
                      
                      // Update the variation content in moves array immediately
                      if (updateVariationContent) updateVariationContent(cleanedContent);
                    }}
                    onBlur={async (e) => {
                      // Force save the current value before losing focus
                      const currentValue = e.target.value;
                      if (updateVariationContent && currentValue !== variationContent) {
                        await updateVariationContent(currentValue);
                      }
                      
                      // Wait a bit more to ensure the Redux state is fully updated
                      setTimeout(() => {
                        if (setIsAnnotationFocused) setIsAnnotationFocused(false);
                        if (setSkipMoveIndexUpdate) setSkipMoveIndexUpdate(false);
                      }, 100); // Reduced timeout since we're now awaiting the save
                    }}
                    onFocus={() => {
                      if (setIsAnnotationFocused) setIsAnnotationFocused(true);
                      if (setSkipMoveIndexUpdate) setSkipMoveIndexUpdate(true);
                    }}
                    onKeyDown={(e) => {
                      // Directly stop propagation for arrow keys and spacebar
                      if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
                        // Allow the default behavior (cursor movement) but prevent the event from bubbling up
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
              )}

              {/* The Classic Trio Display (Previous, Current, Next) */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'row', 
                gap: 24, 
                justifyContent: currentLayer.from ? 'flex-start' : 'space-around', // Main line: space-around, Variation: flex-start
                padding: '8px 12px',
                height: '30px',
                borderRadius: 4,
                borderLeft: getParentBorderColor()
              }}>
                {/* Variation label - only show once at the beginning of variation rows */}
                {currentLayer.from && (() => {
                  const variationInfo = getVariationInfo();
                  const parts = currentMove.split('|');
                  const isNestedVariation = parts.length > 2; // More than main|variation means nested
                  
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', minWidth: 'fit-content', justifyContent: 'flex-start' }}>
                      <span style={{ color: variationInfo.isCommentVariation ? '#ff6600' : 'gold', fontSize: '1.5rem', fontWeight: 'bold' }}>
                       <i className={`fa-solid ${variationInfo.isCommentVariation ? 'fa-comment' : 'fa-circle-user'}`} style={{ color: variationInfo.isCommentVariation ? '#ff6600' : 'gold' }}></i>&nbsp;&nbsp;{isNestedVariation ? 'Nested Variation:' : 'Variation:'}
                      </span>
                    </div>
                  );
                })()}
                
                {/* START button - only show in main line, not in variations */}
                {!currentFocus.prev && !currentLayer.from && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ position: 'relative' }}>
                      <button 
                        onClick={() => goToMove('-1')}
                        style={{
                          background: currentMove === '-1' ? '#58b047' : '#666',
                          color: 'white',
                          padding: mobileStyles.moveButton.padding,
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: mobileStyles.moveButton.fontSize,
                          minWidth: mobileStyles.moveButton.minWidth
                        }}
                      >
                        START
                      </button>
                      
                      {/* ENHANCED: Branch indicator for start position variations */}
                      {state?.startPosition?.variations && state.startPosition.variations.length > 0 && !isStartPositionVariation && (
                        <i className="fa-solid fa-code-branch" 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Navigate to first start position variation
                          goToMove('-1|0:0');
                        }}
                        style={{
                          position: 'absolute',
                          bottom: -10,
                          right: '-25%',
                          width: 8,
                          height: 8,
                          color: getStartPositionVariationColor(),
                          transform: 'rotate(90deg)', // 90 degrees clockwise rotation
                          cursor: 'pointer' // Make it clear it's clickable
                        }}
                        title="Enter first start position variation"
                        ></i>
                      )}
                    </div>
                    
                    {/* Starting Position Variations */}
                    {/* {state?.startPosition?.variations && state.startPosition.variations.length > 0 && (
                      <div style={{ marginLeft: 8 }}>
                        {state.startPosition.variations.map((variation, varIdx) => (
                          <div key={`start-var-${varIdx}`} style={{ display: 'inline-block', marginRight: 4 }}>
                            <button
                              onClick={() => goToMove(`-1|${varIdx}:0`)}
                              style={{
                                background: currentMove.startsWith(`-1|${varIdx}:`) ? '#ff6600' : '#666',
                                color: 'white',
                                padding: '2px 6px',
                                border: 'none',
                                borderRadius: 3,
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                marginRight: 2
                              }}
                              title={`Starting position variation ${varIdx + 1}`}
                            >
                              <i className="fa-solid fa-code-branch" style={{ fontSize: '0.7rem' }}></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )} */}
                    
                    {/* Starting Position Comment Indicator */}
                    {state?.startPosition?.content && state.startPosition.content.trim() && (
                      <div style={{ marginLeft: 4 }}>
                        <i 
                          className="fa-solid fa-comment" 
                          style={{ 
                            color: '#ff6600', 
                            fontSize: '0.8rem',
                            cursor: 'pointer'
                          }}
                          title="Starting position has comments"
                        ></i>
                      </div>
                    )}
                  </div>
                )}
                
                {currentFocus.prev && !isStartPositionVariation && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {renderTrio(currentFocus.prev, currentLayer.from ? `${currentLayer.from}|` : '', '#999')}
                  </div>
                )}
                
                {currentFocus.current && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {renderTrio(currentFocus.current, currentLayer.from ? `${currentLayer.from}|` : '', '#ccc')}
                  </div>
                )}
                
                {currentFocus.next && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {renderTrio(currentFocus.next, currentLayer.from ? `${currentLayer.from}|` : '', '#888')}
                  </div>
                )}

                {/* END marker at the end */}
                {!currentFocus.next && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button 
                      style={{
                          background: moves.length === parseInt(currentMove) + 1 ? '#ff0050' : '#666',
                          color: 'white',
                        padding: mobileStyles.moveButton.padding,
                        borderRadius: 4,
                        border: 'none',
                        fontSize: mobileStyles.moveButton.fontSize,
                        minWidth: mobileStyles.moveButton.minWidth,
                        textAlign: 'center'
                      }}
                    >
                      END
                    </button>
                    
                  </div>
                )}
              </div>
              
              {/* Variation Caption Area - Show variation captions in ViewGame mode */}
              {isViewGame && (() => {
                const captionData = isInVariation(currentMove) ? getAnyCaption(currentMove) : null;
                
                if (!captionData) return null;
                
                // Determine styling based on caption type
                const isCommentCaption = captionData.type === 'comment';
                const borderColor = isCommentCaption ? '#ff6600' : 'gold';
                const iconColor = isCommentCaption ? '#ff6600' : 'gold';
                const icon = isCommentCaption ? 'fa-comment' : 'fa-circle-user';
                const label = isCommentCaption ? 'Comment' : 'Caption';
                
                return (
                  <div style={{
                    borderLeft: `3px solid ${borderColor}`,
                    borderRadius: 4,
                    padding: '4px 12px',
                    fontSize: '14px',
                    color: '#eee',
                    fontWeight: '',
                    textAlign: 'left',
                    overflow: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${borderColor}, transparent`,
                  }}>
                    <span style={{ color: iconColor, fontWeight: 'bold'}}>
                     {label}:&nbsp;&nbsp;
                    </span>
                    {cleanProfanity(captionData.caption)}
                  </div>
                );
              })()}


              
              {/* Move Content - Conditional rendering based on isViewGame */}
              {isViewGame ? (
                // ViewGame: Display move content (read-only)
                (() => {
                  const content = getCurrentMoveContent(currentMove);
                  const shouldShow = content;
                  
                  
                  return shouldShow ? (
                    <div style={{
                      borderLeft: '3px solid #58b047',
                     
                     
                      borderRadius: 4,
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#eee',
                      lineHeight: '1.4'
                    }}>
                      <div style={{  color: '#58b047', fontWeight: 'bold' }}>
                        Move Annotation: <span style={{  color: '#eee', fontWeight: 'normal' }}>&nbsp;{cleanProfanity(content)}</span>
                      </div>
                      
                    </div>
                  ) : null;
                })()
              ) : (
                // AddGame: Editable move content textarea with star icon
                <div style={{ position: 'relative', width: '100%' }}>
                  <textarea
                    placeholder="Annotate this move..."
                    maxLength="1000"
                    style={{
                      height: '46px',
                      padding: '10px',
                      backgroundColor: '#222',
                      color: 'white',
                      border: '2px solid #57b047',
                      borderRadius: '3px',
                      fontSize: '16px',
                      resize: 'none',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'gold #222',
                      width: '96%',
                      ...annotationStyleOverride
                    }}
                    value={moveContent}
                    onChange={(e) => {
                      // Preemptively set both flags to block any board state changes
                      if (setIsAnnotationFocused) setIsAnnotationFocused(true);
                      if (setSkipMoveIndexUpdate) setSkipMoveIndexUpdate(true);
                      
                      const newContent = e.target.value;
                      const cleanedContent = cleanProfanity(newContent);
                      if (setMoveContent) setMoveContent(cleanedContent);
                      
                      // Update the content in moves array immediately
                      if (updateContentForMove) updateContentForMove(cleanedContent);
                      
                      // Reset skipMoveIndexUpdate after a longer delay to allow state to settle
                      setTimeout(() => {
                        setTimeout(() => {
                          if (setSkipMoveIndexUpdate) setSkipMoveIndexUpdate(false);
                        }, 500); // Longer second delay
                      }, 100);
                    }}
                    onBlur={(e) => {
                      // Use a timeout to prevent immediate processing of move changes
                      setTimeout(() => {
                        if (setIsAnnotationFocused) setIsAnnotationFocused(false);
                        if (setSkipMoveIndexUpdate) setSkipMoveIndexUpdate(false);
                      }, 200);
                    }}
                    onFocus={() => {
                      if (setIsAnnotationFocused) setIsAnnotationFocused(true);
                      if (setSkipMoveIndexUpdate) setSkipMoveIndexUpdate(true);
                    }}
                    onKeyDown={(e) => {
                      // Directly stop propagation for arrow keys and spacebar
                      if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
                        // Allow the default behavior (cursor movement) but prevent the event from bubbling up
                        e.stopPropagation();
                      }
                    }}
                  />
                  
                  {/* Star Icon - Only show in AddGame mode (isViewGame=false) */}
                  <i
                    className={clsx(
                      'fa-star',
                      'reg-buttons',
                      starredMoves && starredMoves.includes(currentMoveIdx)
                        ? 'fa-solid'
                        : 'fa-regular'
                    )}
                    style={{
                      color: '#ffd700',
                      fontSize: '24px',
                      position: 'absolute',
                      bottom: '15px',
                      right: '10px',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      
                      // Use the currentMove from context for starring
                      const moveId = currentMove !== '-1' ? currentMove : currentMoveIdx;
                      if (moveId !== '-1' && dispatch) {
                        dispatch({ type: 'TOGGLE_STAR_MOVE', payload: moveId });
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Comments Indicator Section */}
      <div style={{
        padding: '0 12px 8px 12px'
      }}>
        {/* Move-linked comments indicator - clickable */}
        {(() => {
          // Check if we should show the comments indicator
          const shouldShowIndicator = () => {
            // Always show if we're on main line or start position
            if (!currentMove.includes('|') || currentMove === '-1') {
              return hasLinkedComments(currentMove, comments, moves);
            }
            
            // We're in a variation - only show if THIS SPECIFIC move has comments
            // (not just the parent move that branches to this variation)
            
            // Check for move-linked comments specifically for this variation move
            const hasDirectMoveLinkedComments = comments && Array.isArray(comments) && comments.some(comment => 
              comment.moveIndex === currentMove && 
              comment.moveIndex !== undefined && 
              comment.moveIndex !== null
            );
            
            // Check if this specific variation move has comment variations
            const hasDirectCommentVariations = (() => {
              try {
                const nodeResult = getNodeAtPathInfinite(moves, currentMove);
                if (!nodeResult.success || !nodeResult.node?.variations) {
                  return false;
                }
                
                return nodeResult.node.variations.some(variation => 
                  variation.some(varMove => 
                    varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
                  )
                );
              } catch (error) {
                return false;
              }
            })();
            
            return hasDirectMoveLinkedComments || hasDirectCommentVariations;
          };
          
          return shouldShowIndicator() ? (
            <div 
              onClick={() => {
                // Trigger move-focused comment sorting and open social tab
                window.dispatchEvent(new CustomEvent('focus-move-comments', {
                  detail: { moveIndex: currentMove }
                }));
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: mobileStyles.moveButton.padding,
                background: 'rgba(255, 165, 0, 0.2)',
                border: '1px solid #FFA500',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginTop: '10px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 165, 0, 0.3)';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 165, 0, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <i className="fa-solid fa-comment" style={{ color: '#FFA500', fontSize: '14px' }}></i>
                          <span style={{ color: '#FFA500', fontSize: '14px', fontWeight: 'bold' }}>
              {(() => {
                // When in a variation, only count comments directly related to this variation move
                if (currentMove.includes('|')) {
                  // Count move-linked comments specifically for this variation move
                  const directMoveLinkedCount = getLinkedCommentsCount(currentMove, comments);
                  
                  // Count comment variations that branch from this specific variation move
                  const directCommentVariationsCount = getCommentVariationsCount(currentMove, moves);
                  
                  const totalCount = directMoveLinkedCount + directCommentVariationsCount;
                  return `${totalCount} Comment${totalCount !== 1 ? 's' : ''} At This Position`;
                } else {
                  // For main line moves, use the full count as before
                  const totalCount = getTotalCommentsCount(currentMove, comments, moves);
                  return `${totalCount} Comment${totalCount !== 1 ? 's' : ''} At This Position`;
                }
              })()}
            </span>
            </div>
          ) : null;
        })()}
      </div>

      {/* Variations Section */}
      <div style={{
        flex: 1,
        padding: '0 12px 12px 12px'
      }}>
        {/* Variation Previews */}
        {renderVariationPreview()}
      </div>
    </div>
  );
};

export default Moves;